import React from 'react';
import { useLocation } from 'react-router-dom';
import { getLangFromPath } from '../i18n/lang';
import './ApiPage.css';

const endpointUrl = 'https://stat-ingest.radiomonitor.net/ingest/v1/listeners/snapshot';
const scheduleEndpointUrl = 'https://stat-ingest.radiomonitor.net/ingest/v1/stations/{shortcode}/schedule/import';
const schedulePreviewEndpointUrl = 'https://stat-ingest.radiomonitor.net/ingest/v1/schedule/preview';

const requestExample = `{
  "snapshot_id": "2026-05-13T16:39:22Z-radio-test-001",
  "snapshot_ts": "2026-05-13T16:39:22Z",
  "streams": [
    {
      "stream_id": "/radio-main",
      "listeners": [
        {
          "listener_id": "5756",
          "ip_address": "89.208.222.207",
          "connected_sec": 701,
          "user_agent": "Icecast 2.4.4"
        }
      ]
    }
  ]
}`;

const responseExample = `{
  "ok": true,
  "channel_code": "radio_example_101",
  "snapshot_id": "2026-05-13T16:39:22Z-radio-test-001",
  "streams_received": 1,
  "listeners_received": 1,
  "sessions_created": 1,
  "sessions_updated": 0,
  "sessions_closed": 0,
  "listener_ips_updated": 1
}`;

const errorExample = `{
  "ok": false,
  "error": "invalid_payload"
}`;

const errorCodes = [
  ['400', 'invalid_json'],
  ['400', 'invalid_payload'],
  ['400', 'duplicate_stream_id'],
  ['400', 'duplicate_listener_id'],
  ['401', 'unauthorized'],
  ['403', 'key_disabled'],
  ['403', 'forbidden_ip']
];

const translations = {
  uk: {
    sidebarTitle: 'На цій сторінці',
    sidebar: [
      ['overview', 'Огляд'],
      ['connection', 'Як працює підключення'],
      ['authentication', 'Авторизація'],
      ['endpoint', 'Endpoint'],
      ['station-key', 'Один API key = одна станція'],
      ['request-body', 'Тіло запиту'],
      ['request-example', 'Приклад запиту'],
      ['response-example', 'Приклад відповіді'],
      ['errors', 'Помилки'],
      ['sender-ip', 'Безпека IP відправника'],
      ['direct-vs-sender', 'Direct API vs Sender'],
      ['support', 'Підтримка']
    ],
    heroKicker: 'Документація для розробників Radiomonitor',
    heroTitle: 'Radiomonitor Ingest API',
    heroText: 'Надсилайте поточні snapshot слухачів із технічної системи радіостанції до Radiomonitor через простий JSON API.',
    overview: {
      eyebrow: 'Огляд',
      title: 'Для чого цей API',
      text: 'Radiomonitor приймає поточні listener snapshots через JSON API. Цей спосіб підключення підходить, якщо у радіостанції є розробник або власна технічна система, яка вже знає поточних слухачів.',
      tiles: [
        ['Вхідні дані', 'Поточні слухачі', 'Повний snapshot активних слухачів, згрупованих за stream.'],
        ['Формат', 'JSON over HTTPS', 'Структуровані payloads, які надсилає ваша технічна система.'],
        ['Призначення', 'Статистика Radiomonitor', 'Автоматичне підключення станції після першого валідного snapshot.']
      ]
    },
    connection: {
      eyebrow: 'Flow',
      title: 'Як працює підключення',
      steps: [
        ['Додайте радіостанцію в кабінеті', 'Створіть запис станції, яка буде отримувати дані слухачів.'],
        ['Отримайте API endpoint та API key', 'Ці дані генеруються для однієї конкретної радіостанції.'],
        ['Передайте їх технічному спеціалісту', 'Ваша система має регулярно надсилати JSON snapshot слухачів.'],
        ['Станція підключиться автоматично', 'Після першого коректного snapshot Radiomonitor позначить станцію як підключену.']
      ]
    },
    authentication: {
      eyebrow: 'Безпека',
      title: 'Авторизація',
      textBefore: 'Кожен запит має містити API key станції в header',
      textAfter: 'і надсилатися як JSON з',
      cardLabel: 'Обов’язкові headers'
    },
    endpoint: {
      eyebrow: 'Endpoint',
      title: 'Endpoint listener snapshot',
      text: 'Надішліть один повний listener snapshot для однієї радіостанції. Відповідь підтверджує, скільки streams і listeners було прийнято.'
    },
    stationKey: {
      eyebrow: 'Модель ключа',
      title: 'Один API key = одна станція',
      text1Before: 'Один API key призначений рівно для однієї радіостанції. Кілька',
      text1After: 'можна надсилати під одним ключем лише тоді, коли це один і той самий ефір у різних форматах або варіантах доставки.',
      text2: 'Не можна змішувати різні радіостанції в одному snapshot. Якщо дані мають надсилати кілька станцій, кожна станція повинна мати власний API key.'
    },
    requestBody: {
      eyebrow: 'Payload',
      title: 'Тіло запиту',
      columns: ['Поле', 'Тип', 'Обов’язково', 'Опис'],
      yes: 'так',
      no: 'ні',
      descriptions: {
        snapshot_id: 'Унікальний ідентифікатор snapshot, який генерує відправник.',
        snapshot_ts: 'Час snapshot у форматі ISO 8601, бажано UTC.',
        'streams[]': 'Список streams або mountpoints у цьому snapshot.',
        'streams[].stream_id': 'Ідентифікатор stream або mountpoint, наприклад /radio-main.',
        'streams[].listeners[]': 'Активні слухачі для цього stream.',
        'streams[].listeners[].listener_id': 'Стабільний ідентифікатор слухача всередині snapshot stream.',
        'streams[].listeners[].ip_address': 'IP-адреса слухача.',
        'streams[].listeners[].connected_sec': 'Скільки секунд слухач уже підключений.',
        'streams[].listeners[].user_agent': 'Необов’язковий client або source user agent слухача.'
      }
    },
    examples: {
      requestEyebrow: 'Приклад',
      requestTitle: 'Приклад запиту',
      responseEyebrow: 'Приклад',
      responseTitle: 'Приклад відповіді'
    },
    errors: {
      eyebrow: 'Помилки',
      title: 'Відповіді з помилками'
    },
    senderIp: {
      eyebrow: 'Мережева безпека',
      title: 'Безпека IP відправника',
      p1: 'IP відправника не потрібно вказувати вручну. Після періоду налаштування Radiomonitor автоматично фіксує IP відправника, який реально використовується.',
      p2: 'Якщо інтеграцію пізніше переносять на інший сервер і IP змінюється, зверніться до підтримки, щоб оновити підключення.'
    },
    directVsSender: {
      eyebrow: 'Варіанти інтеграції',
      title: 'Direct API vs Sender',
      directTitle: 'Direct API',
      directText: 'Основний і найбільш універсальний спосіб підключення, особливо для HTTPS proxy, CDN, HLS та нестандартних схем доставки.',
      senderTitle: 'Radiomonitor Sender',
      senderText: 'Допоміжний інструмент для простих Icecast-схем, коли Icecast уже показує реальні IP-адреси слухачів.'
    },
    support: {
      eyebrow: 'Допомога',
      title: 'Підтримка',
      text: 'З питань production-підключення, API key або зміни IP відправника звертайтеся до підтримки Radiomonitor:'
    },
    quick: {
      title: 'Швидка довідка',
      method: 'Метод',
      endpoint: 'Endpoint',
      headers: 'Headers',
      link: 'Дивитися приклад запиту'
    }
  },
  en: {
    sidebarTitle: 'On this page',
    sidebar: [
      ['overview', 'Overview'],
      ['connection', 'How connection works'],
      ['authentication', 'Authentication'],
      ['endpoint', 'Endpoint'],
      ['station-key', 'One API key = one station'],
      ['request-body', 'Request body'],
      ['request-example', 'Request example'],
      ['response-example', 'Response example'],
      ['errors', 'Error responses'],
      ['sender-ip', 'Sender IP security'],
      ['direct-vs-sender', 'Direct API vs Sender'],
      ['support', 'Support']
    ],
    heroKicker: 'Radiomonitor developer docs',
    heroTitle: 'Radiomonitor Ingest API',
    heroText: 'Send current listener snapshots from your station-side system to Radiomonitor through a simple JSON API.',
    overview: {
      eyebrow: 'Overview',
      title: 'What this API is for',
      text: 'Radiomonitor accepts current listener snapshots through a JSON API. This connection method is best when a radio station has a developer or its own technical system that already knows who is listening right now.',
      tiles: [
        ['Input', 'Current listeners', 'A full snapshot of active listeners grouped by stream.'],
        ['Format', 'JSON over HTTPS', 'Structured payloads sent by your own technical system.'],
        ['Use case', 'Radiomonitor statistics', 'Automatic station connection after the first valid snapshot.']
      ]
    },
    connection: {
      eyebrow: 'Flow',
      title: 'How connection works',
      steps: [
        ['Add the radio station in your account', 'Create the station record that will receive listener data.'],
        ['Get the API endpoint and API key', 'These credentials are generated for one specific station.'],
        ['Pass them to your technical specialist', 'Your system should regularly send JSON listener snapshots.'],
        ['The station becomes connected automatically', 'After the first valid snapshot, Radiomonitor marks the station as connected.']
      ]
    },
    authentication: {
      eyebrow: 'Security',
      title: 'Authentication',
      textBefore: 'Each request must include the station API key in the',
      textAfter: 'header and must be sent as JSON with',
      cardLabel: 'Required headers'
    },
    endpoint: {
      eyebrow: 'Endpoint',
      title: 'Listener snapshot endpoint',
      text: 'Send one complete listener snapshot for one radio station. The response confirms how many streams and listeners were accepted.'
    },
    stationKey: {
      eyebrow: 'Key model',
      title: 'One API key = one station',
      text1Before: 'One API key is intended for exactly one radio station. Multiple',
      text1After: 'values may be sent under one key only when they represent the same broadcast in different formats or delivery variants.',
      text2: 'Do not mix different radio stations in one snapshot. If several stations need to send data, each station must use its own API key.'
    },
    requestBody: {
      eyebrow: 'Payload',
      title: 'Request body',
      columns: ['Field', 'Type', 'Required', 'Description'],
      yes: 'yes',
      no: 'no',
      descriptions: {
        snapshot_id: 'Unique snapshot identifier generated by the sender.',
        snapshot_ts: 'ISO 8601 timestamp, preferably UTC.',
        'streams[]': 'List of streams or mountpoints in this snapshot.',
        'streams[].stream_id': 'Stream or mountpoint identifier, for example /radio-main.',
        'streams[].listeners[]': 'Active listeners for this stream.',
        'streams[].listeners[].listener_id': 'Stable listener identifier inside the stream snapshot.',
        'streams[].listeners[].ip_address': 'Listener IP address.',
        'streams[].listeners[].connected_sec': 'How long the listener has been connected, in seconds.',
        'streams[].listeners[].user_agent': 'Optional listener client or source user agent.'
      }
    },
    examples: {
      requestEyebrow: 'Example',
      requestTitle: 'Request example',
      responseEyebrow: 'Example',
      responseTitle: 'Response example'
    },
    errors: {
      eyebrow: 'Errors',
      title: 'Error responses'
    },
    senderIp: {
      eyebrow: 'Network security',
      title: 'Sender IP security',
      p1: 'You do not need to enter sender IP addresses manually. After the setup period, Radiomonitor automatically locks the integration to the sender IP address that is actually used.',
      p2: 'If the integration is later moved to another server and the sender IP changes, contact support so the connection can be updated.'
    },
    directVsSender: {
      eyebrow: 'Integration options',
      title: 'Direct API vs Sender',
      directTitle: 'Direct API',
      directText: 'The main and most universal connection method, especially for HTTPS proxy, CDN, HLS, and non-standard delivery schemes.',
      senderTitle: 'Radiomonitor Sender',
      senderText: 'A helper tool for simple Icecast setups where Icecast already shows the real listener IP addresses.'
    },
    support: {
      eyebrow: 'Help',
      title: 'Support',
      text: 'For production connection details, API key questions, or sender IP changes, contact Radiomonitor support:'
    },
    quick: {
      title: 'Quick reference',
      method: 'Method',
      endpoint: 'Endpoint',
      headers: 'Headers',
      link: 'View request example'
    }
  },
  it: {
    sidebarTitle: 'In questa pagina',
    sidebar: [
      ['overview', 'Panoramica'],
      ['connection', 'Come funziona la connessione'],
      ['authentication', 'Autenticazione'],
      ['endpoint', 'Endpoint'],
      ['station-key', 'Una API key = una stazione'],
      ['request-body', 'Corpo della richiesta'],
      ['request-example', 'Esempio di richiesta'],
      ['response-example', 'Esempio di risposta'],
      ['errors', 'Risposte di errore'],
      ['sender-ip', 'Sicurezza IP del mittente'],
      ['direct-vs-sender', 'Direct API vs Sender'],
      ['support', 'Supporto']
    ],
    heroKicker: 'Documentazione per sviluppatori Radiomonitor',
    heroTitle: 'Radiomonitor Ingest API',
    heroText: 'Invia snapshot correnti degli ascoltatori dal sistema tecnico della stazione a Radiomonitor tramite una semplice API JSON.',
    overview: {
      eyebrow: 'Panoramica',
      title: 'A cosa serve questa API',
      text: 'Radiomonitor riceve snapshot correnti degli ascoltatori tramite una API JSON. Questo metodo di connessione è adatto quando una radio ha uno sviluppatore o un proprio sistema tecnico che conosce già gli ascoltatori correnti.',
      tiles: [
        ['Input', 'Ascoltatori correnti', 'Uno snapshot completo degli ascoltatori attivi raggruppati per stream.'],
        ['Formato', 'JSON over HTTPS', 'Payload strutturati inviati dal vostro sistema tecnico.'],
        ['Caso d’uso', 'Statistiche Radiomonitor', 'Connessione automatica della stazione dopo il primo snapshot valido.']
      ]
    },
    connection: {
      eyebrow: 'Flow',
      title: 'Come funziona la connessione',
      steps: [
        ['Aggiungi la radio nel tuo account', 'Crea la scheda della stazione che riceverà i dati degli ascoltatori.'],
        ['Ottieni API endpoint e API key', 'Queste credenziali vengono generate per una sola stazione specifica.'],
        ['Consegnali al tecnico', 'Il vostro sistema deve inviare regolarmente snapshot JSON degli ascoltatori.'],
        ['La stazione viene collegata automaticamente', 'Dopo il primo snapshot corretto, Radiomonitor contrassegna la stazione come collegata.']
      ]
    },
    authentication: {
      eyebrow: 'Sicurezza',
      title: 'Autenticazione',
      textBefore: 'Ogni richiesta deve includere la API key della stazione nell’header',
      textAfter: 'e deve essere inviata come JSON con',
      cardLabel: 'Header obbligatori'
    },
    endpoint: {
      eyebrow: 'Endpoint',
      title: 'Endpoint listener snapshot',
      text: 'Invia uno snapshot completo degli ascoltatori per una sola stazione radio. La risposta conferma quanti stream e ascoltatori sono stati accettati.'
    },
    stationKey: {
      eyebrow: 'Modello della chiave',
      title: 'Una API key = una stazione',
      text1Before: 'Una API key è destinata esattamente a una sola stazione radio. Più valori',
      text1After: 'possono essere inviati con la stessa chiave solo se rappresentano lo stesso programma in formati diversi o varianti di distribuzione.',
      text2: 'Non mescolare stazioni radio diverse nello stesso snapshot. Se più stazioni devono inviare dati, ogni stazione deve usare la propria API key.'
    },
    requestBody: {
      eyebrow: 'Payload',
      title: 'Corpo della richiesta',
      columns: ['Campo', 'Tipo', 'Obbligatorio', 'Descrizione'],
      yes: 'sì',
      no: 'no',
      descriptions: {
        snapshot_id: 'Identificatore univoco dello snapshot generato dal mittente.',
        snapshot_ts: 'Timestamp ISO 8601, preferibilmente in UTC.',
        'streams[]': 'Elenco degli stream o mountpoint in questo snapshot.',
        'streams[].stream_id': 'Identificatore dello stream o mountpoint, per esempio /radio-main.',
        'streams[].listeners[]': 'Ascoltatori attivi per questo stream.',
        'streams[].listeners[].listener_id': 'Identificatore stabile dell’ascoltatore nello snapshot dello stream.',
        'streams[].listeners[].ip_address': 'Indirizzo IP dell’ascoltatore.',
        'streams[].listeners[].connected_sec': 'Da quanti secondi l’ascoltatore è connesso.',
        'streams[].listeners[].user_agent': 'Client opzionale o source user agent dell’ascoltatore.'
      }
    },
    examples: {
      requestEyebrow: 'Esempio',
      requestTitle: 'Esempio di richiesta',
      responseEyebrow: 'Esempio',
      responseTitle: 'Esempio di risposta'
    },
    errors: {
      eyebrow: 'Errori',
      title: 'Risposte di errore'
    },
    senderIp: {
      eyebrow: 'Sicurezza di rete',
      title: 'Sicurezza IP del mittente',
      p1: 'Non è necessario inserire manualmente gli indirizzi IP del mittente. Dopo il periodo di configurazione, Radiomonitor blocca automaticamente l’integrazione sull’indirizzo IP del mittente effettivamente utilizzato.',
      p2: 'Se in seguito l’integrazione viene spostata su un altro server e l’IP del mittente cambia, contatta il supporto per aggiornare la connessione.'
    },
    directVsSender: {
      eyebrow: 'Opzioni di integrazione',
      title: 'Direct API vs Sender',
      directTitle: 'Direct API',
      directText: 'Il metodo principale e più universale, soprattutto per HTTPS proxy, CDN, HLS e schemi di distribuzione non standard.',
      senderTitle: 'Radiomonitor Sender',
      senderText: 'Uno strumento di supporto per configurazioni Icecast semplici, quando Icecast mostra già i veri indirizzi IP degli ascoltatori.'
    },
    support: {
      eyebrow: 'Aiuto',
      title: 'Supporto',
      text: 'Per dettagli sulla connessione in produzione, domande sulla API key o modifiche dell’IP del mittente, contatta il supporto Radiomonitor:'
    },
    quick: {
      title: 'Riferimento rapido',
      method: 'Metodo',
      endpoint: 'Endpoint',
      headers: 'Header',
      link: 'Vedi esempio di richiesta'
    }
  },
  fr: {
    sidebarTitle: 'Sur cette page',
    sidebar: [
      ['overview', 'Vue d’ensemble'],
      ['connection', 'Fonctionnement de la connexion'],
      ['authentication', 'Authentification'],
      ['endpoint', 'Endpoint'],
      ['station-key', 'Une API key = une station'],
      ['request-body', 'Corps de la requête'],
      ['request-example', 'Exemple de requête'],
      ['response-example', 'Exemple de réponse'],
      ['errors', 'Réponses d’erreur'],
      ['sender-ip', 'Sécurité IP de l’expéditeur'],
      ['direct-vs-sender', 'Direct API vs Sender'],
      ['support', 'Support']
    ],
    heroKicker: 'Documentation développeur Radiomonitor',
    heroTitle: 'Radiomonitor Ingest API',
    heroText: 'Envoyez les snapshots actuels des auditeurs depuis le système technique de la station vers Radiomonitor via une simple API JSON.',
    overview: {
      eyebrow: 'Vue d’ensemble',
      title: 'À quoi sert cette API',
      text: 'Radiomonitor accepte les snapshots actuels des auditeurs via une API JSON. Cette méthode de connexion convient lorsqu’une radio dispose d’un développeur ou d’un système technique qui connaît déjà les auditeurs actuels.',
      tiles: [
        ['Entrée', 'Auditeurs actuels', 'Un snapshot complet des auditeurs actifs regroupés par stream.'],
        ['Format', 'JSON over HTTPS', 'Des payloads structurés envoyés par votre système technique.'],
        ['Cas d’usage', 'Statistiques Radiomonitor', 'Connexion automatique de la station après le premier snapshot valide.']
      ]
    },
    connection: {
      eyebrow: 'Flow',
      title: 'Fonctionnement de la connexion',
      steps: [
        ['Ajoutez la radio dans votre compte', 'Créez la fiche de la station qui recevra les données d’audience.'],
        ['Obtenez l’API endpoint et l’API key', 'Ces identifiants sont générés pour une seule station précise.'],
        ['Transmettez-les au spécialiste technique', 'Votre système doit envoyer régulièrement des snapshots JSON des auditeurs.'],
        ['La station est connectée automatiquement', 'Après le premier snapshot correct, Radiomonitor marque la station comme connectée.']
      ]
    },
    authentication: {
      eyebrow: 'Sécurité',
      title: 'Authentification',
      textBefore: 'Chaque requête doit inclure l’API key de la station dans le header',
      textAfter: 'et être envoyée en JSON avec',
      cardLabel: 'Headers obligatoires'
    },
    endpoint: {
      eyebrow: 'Endpoint',
      title: 'Endpoint listener snapshot',
      text: 'Envoyez un snapshot complet des auditeurs pour une seule station radio. La réponse confirme combien de streams et d’auditeurs ont été acceptés.'
    },
    stationKey: {
      eyebrow: 'Modèle de clé',
      title: 'Une API key = une station',
      text1Before: 'Une API key est destinée à une seule station radio. Plusieurs valeurs',
      text1After: 'peuvent être envoyées avec la même clé uniquement si elles représentent le même programme dans différents formats ou variantes de diffusion.',
      text2: 'Ne mélangez pas plusieurs stations radio dans un même snapshot. Si plusieurs stations doivent envoyer des données, chaque station doit utiliser sa propre API key.'
    },
    requestBody: {
      eyebrow: 'Payload',
      title: 'Corps de la requête',
      columns: ['Champ', 'Type', 'Obligatoire', 'Description'],
      yes: 'oui',
      no: 'non',
      descriptions: {
        snapshot_id: 'Identifiant unique du snapshot généré par l’expéditeur.',
        snapshot_ts: 'Horodatage ISO 8601, de préférence en UTC.',
        'streams[]': 'Liste des streams ou mountpoints dans ce snapshot.',
        'streams[].stream_id': 'Identifiant du stream ou mountpoint, par exemple /radio-main.',
        'streams[].listeners[]': 'Auditeurs actifs pour ce stream.',
        'streams[].listeners[].listener_id': 'Identifiant stable de l’auditeur dans le snapshot du stream.',
        'streams[].listeners[].ip_address': 'Adresse IP de l’auditeur.',
        'streams[].listeners[].connected_sec': 'Durée de connexion de l’auditeur, en secondes.',
        'streams[].listeners[].user_agent': 'Client optionnel ou source user agent de l’auditeur.'
      }
    },
    examples: {
      requestEyebrow: 'Exemple',
      requestTitle: 'Exemple de requête',
      responseEyebrow: 'Exemple',
      responseTitle: 'Exemple de réponse'
    },
    errors: {
      eyebrow: 'Erreurs',
      title: 'Réponses d’erreur'
    },
    senderIp: {
      eyebrow: 'Sécurité réseau',
      title: 'Sécurité IP de l’expéditeur',
      p1: 'Vous n’avez pas besoin d’indiquer manuellement les adresses IP de l’expéditeur. Après la période de configuration, Radiomonitor verrouille automatiquement l’intégration sur l’adresse IP de l’expéditeur réellement utilisée.',
      p2: 'Si l’intégration est ensuite déplacée vers un autre serveur et que l’IP de l’expéditeur change, contactez le support afin de mettre à jour la connexion.'
    },
    directVsSender: {
      eyebrow: 'Options d’intégration',
      title: 'Direct API vs Sender',
      directTitle: 'Direct API',
      directText: 'La méthode principale et la plus universelle, en particulier pour HTTPS proxy, CDN, HLS et les schémas de diffusion non standard.',
      senderTitle: 'Radiomonitor Sender',
      senderText: 'Un outil d’appoint pour les configurations Icecast simples, lorsque Icecast affiche déjà les vraies adresses IP des auditeurs.'
    },
    support: {
      eyebrow: 'Aide',
      title: 'Support',
      text: 'Pour les détails de connexion en production, les questions sur l’API key ou les changements d’IP de l’expéditeur, contactez le support Radiomonitor:'
    },
    quick: {
      title: 'Référence rapide',
      method: 'Méthode',
      endpoint: 'Endpoint',
      headers: 'Headers',
      link: 'Voir l’exemple de requête'
    }
  }
};

const requestFieldOrder = [
  ['snapshot_id', 'string', true],
  ['snapshot_ts', 'ISO datetime', true],
  ['streams[]', 'array', true],
  ['streams[].stream_id', 'string', true],
  ['streams[].listeners[]', 'array', true],
  ['streams[].listeners[].listener_id', 'string', true],
  ['streams[].listeners[].ip_address', 'string', true],
  ['streams[].listeners[].connected_sec', 'number', true],
  ['streams[].listeners[].user_agent', 'string', false]
];

function CodeBlock({ children }) {
  return (
    <pre className="api-code-block">
      <code>{children}</code>
    </pre>
  );
}

function Section({ id, eyebrow, title, children }) {
  return (
    <section className="api-doc-section" id={id}>
      {eyebrow && <div className="api-section-eyebrow">{eyebrow}</div>}
      <h2>{title}</h2>
      {children}
    </section>
  );
}


const scheduleRequestExample = `{
  "radio_id": 125,
  "playlist_file": "schedule_2026-05-25.json",
  "playlist_date": "2026-05-25",
  "blocks": [
    {
      "block_name": "Music block 10:00",
      "block_type": "M",
      "block_type_name": "Music",
      "block_start_time": "10:00:00.000",
      "block_defined_length": "00:30:00.000",
      "block_length": "00:30:00.000",
      "elements": [
        {
          "elem_start_time": "10:00:00.000",
          "elem_end_time": "10:03:30.000",
          "elem_name": "Beyond the Evening Star",
          "elem_length": "00:03:30.000",
          "elem_artist": "Artist name",
          "elem_album": "Album name",
          "elem_class": "M",
          "elem_file": "Artist name - Beyond the Evening Star.mp3",
          "elem_short_file": "Beyond the Evening Star.mp3",
          "elem_category": "Music",
          "elem_order": 1
        },
        {
          "elem_start_time": "10:03:30.000",
          "elem_end_time": "10:07:20.000",
          "elem_name": "Closing Titles From -Mutiny On The Bounty-",
          "elem_length": "00:03:50.000",
          "elem_artist": "Another artist",
          "elem_class": "M",
          "elem_order": 2
        },
        {
          "elem_start_time": "10:25:00.000",
          "elem_end_time": "10:25:07.000",
          "elem_name": "Station jingle",
          "elem_length": "00:00:07.000",
          "elem_class": "J",
          "elem_order": 3
        }
      ]
    },
    {
      "block_name": "Commercial block 10:30",
      "block_type": "C",
      "block_type_name": "Commerce",
      "block_start_time": "10:30:00.000",
      "elements": [
        {
          "elem_start_time": "10:30:00.000",
          "elem_end_time": "10:30:04.000",
          "elem_name": "Commercial intro",
          "elem_class": "J"
        },
        {
          "elem_start_time": "10:30:04.000",
          "elem_end_time": "10:30:34.000",
          "elem_name": "Client spot 30 sec",
          "elem_length": "00:00:30.000",
          "elem_class": "C",
          "external_id": "ad-2026-0001"
        },
        {
          "elem_start_time": "10:30:34.000",
          "elem_end_time": "10:30:38.000",
          "elem_name": "Commercial outro",
          "elem_class": "J"
        }
      ]
    },
    {
      "block_name": "Music block 10:33",
      "block_type": "M",
      "block_type_name": "Music",
      "block_start_time": "10:33:00.000",
      "block_defined_length": "00:27:00.000",
      "block_length": "00:27:00.000",
      "elements": [
        {
          "elem_start_time": "10:33:00.000",
          "elem_end_time": "10:36:42.000",
          "elem_name": "Song after commercial block",
          "elem_length": "00:03:42.000",
          "elem_class": "M",
          "elem_order": 1
        }
      ]
    },
    {
      "block_name": "News bulletin",
      "block_type": "N",
      "block_type_name": "News",
      "block_start_time": "11:00:00.000",
      "elements": [
        {
          "elem_start_time": "11:00:00.000",
          "elem_end_time": "11:00:06.000",
          "elem_name": "News intro",
          "elem_class": "J"
        },
        {
          "elem_start_time": "11:00:06.000",
          "elem_end_time": "11:05:00.000",
          "elem_name": "Main news bulletin",
          "elem_class": "N"
        }
      ]
    },
    {
      "block_name": "Morning show",
      "block_type": "P",
      "block_type_name": "Program",
      "block_start_time": "11:05:00.000",
      "elements": [
        {
          "elem_start_time": "11:05:00.000",
          "elem_end_time": "11:05:06.000",
          "elem_name": "Program intro",
          "elem_class": "J"
        },
        {
          "elem_start_time": "11:05:06.000",
          "elem_end_time": "11:30:00.000",
          "elem_name": "Morning show part 1",
          "elem_class": "B"
        }
      ]
    }
  ]
}`;

const scheduleResponseExample = `{
  "ok": true,
  "station": "karamelka_144",
  "schema": "radio_karamelka_144",
  "table": "radio_schedule",
  "playlist_date": "2026-05-25",
  "blocks_received": 75,
  "items_received": 454,
  "items_inserted": 454,
  "import_batch_id": "7df1aa42-3673-4b7f-9763-50c22a7d2406"
}`;

const scheduleValidationExample = `{
  "ok": false,
  "error": "validation_error",
  "message": "Invalid schedule payload",
  "details": [
    {
      "path": "blocks[0].block_type",
      "message": "invalid block_type. Allowed values: M, P, C, N"
    }
  ]
}`;

const scheduleMissingRadioIdExample = `{
  "ok": false,
  "error": "validation_error",
  "message": "Invalid schedule payload",
  "details": [
    {
      "path": "radio_id",
      "message": "field is required"
    }
  ]
}`;

const scheduleInvalidRadioIdExample = `{
  "ok": false,
  "error": "validation_error",
  "message": "Invalid schedule payload",
  "details": [
    {
      "path": "radio_id",
      "message": "radio_id must be positive integer"
    }
  ]
}`;

const scheduleRadioIdMismatchExample = `{
  "ok": false,
  "error": "radio_id_mismatch",
  "message": "Schedule file does not match API key radio station",
  "details": {
    "api_key_radio_id": 125,
    "file_radio_id": 122
  }
}`;

const scheduleDuplicateExample = `{
  "ok": false,
  "error": "schedule_date_already_imported",
  "message": "Schedule for this date already exists"
}`;

const scheduleSidebar = [
  ['schedule-overview', 'Огляд'],
  ['schedule-flow', 'Як працює імпорт'],
  ['schedule-authentication', 'Авторизація'],
  ['schedule-json-rule', 'Тільки готовий JSON'],
  ['schedule-blocks', 'Типи блоків'],
  ['schedule-elements', 'Класи елементів'],
  ['schedule-mapping', 'Як зіставити свої коди'],
  ['schedule-structure', 'Структура JSON'],
  ['schedule-radio-id', 'Radiomonitor Radio ID'],
  ['schedule-time', 'Час і ефірний день'],
  ['schedule-fields', 'Обов’язкові поля'],
  ['schedule-example', 'Приклад JSON'],
  ['schedule-response', 'Успішна відповідь'],
  ['schedule-errors', 'Помилки'],
  ['schedule-duplicates', 'Повторна дата'],
  ['schedule-mistakes', 'Типові помилки'],
  ['schedule-support', 'Підтримка']
];

const scheduleTopLevelFields = [
  ['radio_id', 'number', true, 'Radiomonitor Radio ID радіостанції. Надсилається користувачу разом з API key і має бути в кожному JSON-файлі розкладу.'],
  ['playlist_file', 'string', true, 'Ім’я або внутрішній ідентифікатор файлу/плейлиста. Це довідкове поле. Джерелом істини для дати є playlist_date.'],
  ['playlist_date', 'date', true, 'Дата ефірного дня у форматі YYYY-MM-DD. Один JSON-файл має містити тільки одну playlist_date.'],
  ['blocks[]', 'array', true, 'Масив блоків ефіру. Блок — це логічна частина розкладу: музика, програма, реклама або новини.']
];

const scheduleBlockFields = [
  ['block_name', 'string', true, 'Вільна назва блоку у вашому розкладі: “Музика 02”, “Рекламний блок”, “Новини”, “Morning show”.'],
  ['block_type', 'string', true, 'Системний тип блоку Radiomonitor: M, P, C або N.'],
  ['block_type_name', 'string', true, 'Системна назва типу блоку: Music, Program, Commerce або News. Має відповідати block_type.'],
  ['block_start_time', 'time', false, 'Час початку блоку, якщо він є у вашому розкладі. Формат HH:MM:SS або HH:MM:SS.mmm.'],
  ['block_defined_length', 'interval', false, 'Планова тривалість блоку, якщо вона є у вашій системі.'],
  ['block_length', 'interval', false, 'Фактична або розрахована тривалість блоку, якщо вона є у вашій системі.'],
  ['elements[]', 'array', true, 'Масив елементів усередині блоку. Кожен елемент стає одним рядком у radio_schedule.']
];

const scheduleElementFields = [
  ['elem_start_time', 'time', true, 'Час початку елемента в ефірному дні.'],
  ['elem_end_time', 'time', true, 'Час завершення елемента в ефірному дні.'],
  ['elem_name', 'string', true, 'Назва елемента: назва треку, ролика, новинного випуску, джинглу або передачі.'],
  ['elem_class', 'string', true, 'Системний клас елемента Radiomonitor: M, J, C, B або N.'],
  ['elem_length', 'interval', false, 'Тривалість елемента.'],
  ['elem_artist', 'string', false, 'Виконавець музичного елемента, якщо є.'],
  ['elem_author', 'string', false, 'Автор, якщо є у вашому розкладі.'],
  ['elem_album', 'string', false, 'Альбом, якщо є у вашому розкладі.'],
  ['elem_file', 'string', false, 'Повний шлях або технічна назва файлу у вашій системі.'],
  ['elem_short_file', 'string', false, 'Коротка назва файлу.'],
  ['elem_category', 'string', false, 'Внутрішня категорія вашої системи.'],
  ['elem_length_to_start_next', 'interval', false, 'Час до старту наступного елемента, якщо ваша система його рахує.'],
  ['elem_id', 'string', false, 'Внутрішній ID елемента у вашій системі.'],
  ['elem_order', 'number', false, 'Порядковий номер елемента в межах блоку або файлу.'],
  ['elem_db_id', 'string', false, 'Внутрішній DB ID елемента у вашій системі.'],
  ['external_id', 'string', false, 'Зовнішній ID, якщо треба пов’язати елемент із вашим рекламним/музичним каталогом.']
];

const scheduleFieldSets = {
  uk: {
    topLevel: scheduleTopLevelFields,
    block: scheduleBlockFields,
    element: scheduleElementFields,
    blockExampleName: 'Новини о 12:00'
  },
  en: {
    topLevel: [
      ['radio_id', 'number', true, 'Radiomonitor Radio ID for the station. It is sent to the user together with the API key and must be present in every schedule JSON file.'],
      ['playlist_file', 'string', true, 'File name or internal playlist identifier. This is a reference field. playlist_date is the source of truth for the date.'],
      ['playlist_date', 'date', true, 'Broadcast day date in YYYY-MM-DD format. One JSON file must contain only one playlist_date.'],
      ['blocks[]', 'array', true, 'Array of broadcast blocks. A block is a logical schedule part: music, program, advertising, or news.']
    ],
    block: [
      ['block_name', 'string', true, 'Free-form block name in your schedule: “Music 02”, “Commercial block”, “News”, “Morning show”.'],
      ['block_type', 'string', true, 'Radiomonitor system block type: M, P, C, or N.'],
      ['block_type_name', 'string', true, 'System block type name: Music, Program, Commerce, or News. Must match block_type.'],
      ['block_start_time', 'time', false, 'Block start time, if it exists in your schedule. Format HH:MM:SS or HH:MM:SS.mmm.'],
      ['block_defined_length', 'interval', false, 'Planned block duration, if your system has it.'],
      ['block_length', 'interval', false, 'Actual or calculated block duration, if your system has it.'],
      ['elements[]', 'array', true, 'Array of elements inside the block. Each element becomes one row in radio_schedule.']
    ],
    element: [
      ['elem_start_time', 'time', true, 'Element start time within the broadcast day.'],
      ['elem_end_time', 'time', true, 'Element end time within the broadcast day.'],
      ['elem_name', 'string', true, 'Element name: track title, spot title, news bulletin, jingle, or program.'],
      ['elem_class', 'string', true, 'Radiomonitor system element class: M, J, C, B, or N.'],
      ['elem_length', 'interval', false, 'Element duration.'],
      ['elem_artist', 'string', false, 'Artist of a music element, if available.'],
      ['elem_author', 'string', false, 'Author, if available in your schedule.'],
      ['elem_album', 'string', false, 'Album, if available in your schedule.'],
      ['elem_file', 'string', false, 'Full path or technical file name in your system.'],
      ['elem_short_file', 'string', false, 'Short file name.'],
      ['elem_category', 'string', false, 'Internal category from your system.'],
      ['elem_length_to_start_next', 'interval', false, 'Time until the next element starts, if your system calculates it.'],
      ['elem_id', 'string', false, 'Internal element ID in your system.'],
      ['elem_order', 'number', false, 'Element order inside the block or file.'],
      ['elem_db_id', 'string', false, 'Internal DB ID of the element in your system.'],
      ['external_id', 'string', false, 'External ID, if the element must be linked to your advertising or music catalog.']
    ],
    blockExampleName: 'News at 12:00'
  },
  it: {
    topLevel: [
      ['radio_id', 'number', true, 'Radiomonitor Radio ID della stazione. Viene inviato all’utente insieme alla API key e deve essere presente in ogni file JSON del palinsesto.'],
      ['playlist_file', 'string', true, 'Nome del file o identificatore interno del palinsesto. È un campo informativo. La data di riferimento è playlist_date.'],
      ['playlist_date', 'date', true, 'Data del giorno di trasmissione in formato YYYY-MM-DD. Un file JSON deve contenere una sola playlist_date.'],
      ['blocks[]', 'array', true, 'Array dei blocchi di trasmissione. Un blocco è una parte logica del palinsesto: musica, programma, pubblicità o notizie.']
    ],
    block: [
      ['block_name', 'string', true, 'Nome libero del blocco nel vostro palinsesto: “Musica 02”, “Blocco pubblicitario”, “Notizie”, “Morning show”.'],
      ['block_type', 'string', true, 'Tipo di blocco di sistema Radiomonitor: M, P, C o N.'],
      ['block_type_name', 'string', true, 'Nome del tipo di blocco: Music, Program, Commerce o News. Deve corrispondere a block_type.'],
      ['block_start_time', 'time', false, 'Ora di inizio del blocco, se presente nel vostro palinsesto. Formato HH:MM:SS o HH:MM:SS.mmm.'],
      ['block_defined_length', 'interval', false, 'Durata pianificata del blocco, se presente nel vostro sistema.'],
      ['block_length', 'interval', false, 'Durata effettiva o calcolata del blocco, se presente nel vostro sistema.'],
      ['elements[]', 'array', true, 'Array degli elementi dentro il blocco. Ogni elemento diventa una riga in radio_schedule.']
    ],
    element: [
      ['elem_start_time', 'time', true, 'Ora di inizio dell’elemento nel giorno di trasmissione.'],
      ['elem_end_time', 'time', true, 'Ora di fine dell’elemento nel giorno di trasmissione.'],
      ['elem_name', 'string', true, 'Nome dell’elemento: titolo del brano, spot, notiziario, jingle o programma.'],
      ['elem_class', 'string', true, 'Classe di sistema Radiomonitor dell’elemento: M, J, C, B o N.'],
      ['elem_length', 'interval', false, 'Durata dell’elemento.'],
      ['elem_artist', 'string', false, 'Artista dell’elemento musicale, se disponibile.'],
      ['elem_author', 'string', false, 'Autore, se disponibile nel vostro palinsesto.'],
      ['elem_album', 'string', false, 'Album, se disponibile nel vostro palinsesto.'],
      ['elem_file', 'string', false, 'Percorso completo o nome tecnico del file nel vostro sistema.'],
      ['elem_short_file', 'string', false, 'Nome breve del file.'],
      ['elem_category', 'string', false, 'Categoria interna del vostro sistema.'],
      ['elem_length_to_start_next', 'interval', false, 'Tempo fino all’inizio dell’elemento successivo, se calcolato dal vostro sistema.'],
      ['elem_id', 'string', false, 'ID interno dell’elemento nel vostro sistema.'],
      ['elem_order', 'number', false, 'Numero d’ordine dell’elemento dentro il blocco o il file.'],
      ['elem_db_id', 'string', false, 'ID DB interno dell’elemento nel vostro sistema.'],
      ['external_id', 'string', false, 'ID esterno, se l’elemento deve essere collegato al vostro catalogo pubblicitario o musicale.']
    ],
    blockExampleName: 'Notiziario delle 12:00'
  },
  fr: {
    topLevel: [
      ['radio_id', 'number', true, 'Radiomonitor Radio ID de la station. Il est envoyé à l’utilisateur avec l’API key et doit être présent dans chaque fichier JSON de planning.'],
      ['playlist_file', 'string', true, 'Nom du fichier ou identifiant interne du planning. C’est un champ informatif. La date de référence est playlist_date.'],
      ['playlist_date', 'date', true, 'Date du jour d’antenne au format YYYY-MM-DD. Un fichier JSON doit contenir une seule playlist_date.'],
      ['blocks[]', 'array', true, 'Tableau des blocs d’antenne. Un bloc est une partie logique du planning: musique, programme, publicité ou infos.']
    ],
    block: [
      ['block_name', 'string', true, 'Nom libre du bloc dans votre planning: “Musique 02”, “Bloc publicitaire”, “Infos”, “Morning show”.'],
      ['block_type', 'string', true, 'Type de bloc système Radiomonitor: M, P, C ou N.'],
      ['block_type_name', 'string', true, 'Nom du type de bloc: Music, Program, Commerce ou News. Il doit correspondre à block_type.'],
      ['block_start_time', 'time', false, 'Heure de début du bloc, si elle existe dans votre planning. Format HH:MM:SS ou HH:MM:SS.mmm.'],
      ['block_defined_length', 'interval', false, 'Durée prévue du bloc, si elle existe dans votre système.'],
      ['block_length', 'interval', false, 'Durée réelle ou calculée du bloc, si elle existe dans votre système.'],
      ['elements[]', 'array', true, 'Tableau des éléments dans le bloc. Chaque élément devient une ligne dans radio_schedule.']
    ],
    element: [
      ['elem_start_time', 'time', true, 'Heure de début de l’élément dans le jour d’antenne.'],
      ['elem_end_time', 'time', true, 'Heure de fin de l’élément dans le jour d’antenne.'],
      ['elem_name', 'string', true, 'Nom de l’élément: titre du morceau, spot, bulletin d’infos, jingle ou programme.'],
      ['elem_class', 'string', true, 'Classe système Radiomonitor de l’élément: M, J, C, B ou N.'],
      ['elem_length', 'interval', false, 'Durée de l’élément.'],
      ['elem_artist', 'string', false, 'Artiste de l’élément musical, si disponible.'],
      ['elem_author', 'string', false, 'Auteur, si disponible dans votre planning.'],
      ['elem_album', 'string', false, 'Album, si disponible dans votre planning.'],
      ['elem_file', 'string', false, 'Chemin complet ou nom technique du fichier dans votre système.'],
      ['elem_short_file', 'string', false, 'Nom court du fichier.'],
      ['elem_category', 'string', false, 'Catégorie interne de votre système.'],
      ['elem_length_to_start_next', 'interval', false, 'Temps jusqu’au début de l’élément suivant, si votre système le calcule.'],
      ['elem_id', 'string', false, 'ID interne de l’élément dans votre système.'],
      ['elem_order', 'number', false, 'Ordre de l’élément dans le bloc ou le fichier.'],
      ['elem_db_id', 'string', false, 'ID DB interne de l’élément dans votre système.'],
      ['external_id', 'string', false, 'ID externe, si l’élément doit être lié à votre catalogue publicitaire ou musical.']
    ],
    blockExampleName: 'Journal de 12:00'
  }
};

const blockTypes = [
  ['M', 'Music', 'Музичний блок', 'Використовуйте для частини ефіру, де основна задача — музика. Усередині можуть бути треки M і джингли J.'],
  ['P', 'Program', 'Програма / передача / шоу', 'Використовуйте для передач, шоу, рубрик, казок, авторських програм або великих програмних елементів.'],
  ['C', 'Commerce', 'Рекламний / комерційний блок', 'Використовуйте для рекламного блоку. Довжина блоку може рахуватися від першого до останнього елемента, але роликами вважаються тільки C.'],
  ['N', 'News', 'Новинний блок', 'Використовуйте для новин. Не використовуйте кириличну Н — потрібна латинська N.']
];

const elementClasses = [
  ['M', 'Music item', 'Музичний трек', 'Пісня, композиція, музичний елемент. Саме ці елементи будуть важливі для TOP та музичних звітів.'],
  ['J', 'Jingle / separator', 'Джингл / заставка / перебивка', 'Інтро, аутро, шапка, відбивка, станційний джингл. Не є треком, рекламою, новиною або передачею.'],
  ['C', 'Commerce item', 'Рекламний ролик', 'Саме рекламний або комерційний елемент усередині рекламного блоку.'],
  ['B', 'Program item', 'Передача / великий програмний елемент', 'Основна частина програми, шоу, казки, рубрики або іншого програмного контенту.'],
  ['N', 'News item', 'Новинний елемент', 'Новина, випуск новин, інформаційний матеріал усередині новинного блоку.']
];

const mappingRows = [
  ['Музыка, Музика, Music, Songs, Песни, М', 'block_type = "M", block_type_name = "Music"'],
  ['Программа, Програма, Show, Program, Передача, П', 'block_type = "P", block_type_name = "Program"'],
  ['Реклама, Ads, Advert, Commercial, Commerce, Р, C', 'block_type = "C", block_type_name = "Commerce"'],
  ['Новости, Новини, News, Н', 'block_type = "N", block_type_name = "News"'],
  ['Песня, Трек, Song, Music item', 'elem_class = "M"'],
  ['Джингл, Jingle, Intro, Outro, Перебивка, Шапка, Отбивка', 'elem_class = "J"'],
  ['Рекламный ролик, Spot, Advert, Commercial', 'elem_class = "C"'],
  ['Передача, Program item, Show item, Long program item', 'elem_class = "B"'],
  ['Новость, News item, Bulletin, News story', 'elem_class = "N"']
];

const scheduleErrorCodes = [
  ['400', 'validation_error'],
  ['403', 'radio_id_mismatch'],
  ['401', 'unauthorized'],
  ['403', 'key_disabled'],
  ['403', 'forbidden_station'],
  ['403', 'forbidden_ip'],
  ['404', 'station_not_found'],
  ['409', 'schedule_date_already_imported']
];

const apiPageUi = {
  uk: {
    title: 'Radiomonitor API Integration',
    tabs: {
      ingest: 'Radiomonitor Ingest API',
      schedule: 'Schedule Import API',
      test: 'Schedule JSON Test'
    }
  },
  en: {
    title: 'Radiomonitor API Integration',
    tabs: {
      ingest: 'Radiomonitor Ingest API',
      schedule: 'Schedule Import API',
      test: 'Schedule JSON Test'
    }
  },
  it: {
    title: 'Integrazione API Radiomonitor',
    tabs: {
      ingest: 'Radiomonitor Ingest API',
      schedule: 'Schedule Import API',
      test: 'Schedule JSON Test'
    }
  },
  fr: {
    title: 'Intégration API Radiomonitor',
    tabs: {
      ingest: 'Radiomonitor Ingest API',
      schedule: 'Schedule Import API',
      test: 'Schedule JSON Test'
    }
  }
};

const scheduleTranslations = {
  uk: {
    kicker: 'Документація для розробників Radiomonitor',
    sidebarTitle: 'На цій сторінці',
    sidebar: scheduleSidebar,
    yes: 'так',
    no: 'ні',
    columns: {
      code: 'Код',
      name: 'Назва',
      meaning: 'Значення',
      usage: 'Як використовувати',
      sourceName: 'Як може називатися у вашій системі',
      targetName: 'Що передавати в Radiomonitor',
      field: 'Поле',
      type: 'Тип',
      required: 'Обов’язково',
      description: 'Опис'
    },
    hero: {
      title: 'Radiomonitor Schedule Import API',
      text: 'Radiomonitor не перетворює TXT, XML, CSV або інші файли у розклад. Ваша система має сама підготувати готовий JSON за форматом Radiomonitor. Schedule JSON Test лише перевіряє цей JSON і показує, як Radiomonitor його бачить. Тест нічого не імпортує і нічого не записує в базу.'
    },
    overview: {
      eyebrow: 'Огляд',
      title: 'Для чого цей API',
      p1: 'Schedule Import API потрібен для завантаження готового добового розкладу радіостанції. На відміну від listener snapshots, розклад не надсилається постійно. Зазвичай це один JSON-файл за один ефірний день.',
      p2: 'Endpoint імпорту є backend/internal integration endpoint. Програміст радіостанції зазвичай готує JSON і перевіряє його через вкладку Schedule JSON Test. Shortcode є внутрішньою сутністю Radiomonitor: користувачу не потрібно знати або вводити його вручну.',
      p3: 'Для перевірки файлу використовуйте Schedule JSON Test, де потрібні тільки API key і JSON-файл. Реальний імпорт виконує кабінет або backend Radiomonitor.',
      tiles: [
        ['Вхідні дані', 'Готовий JSON', 'Розклад уже має бути приведений до стандарту Radiomonitor Schedule API.'],
        ['Один файл', 'Одна дата', 'Один JSON-файл має містити тільки одну playlist_date для однієї радіостанції.'],
        ['Збереження', 'Історична таблиця', 'Після імпорту дані накопичуються у station schema і не замінюються через API.']
      ]
    },
    flow: {
      eyebrow: 'Flow',
      title: 'Як працює імпорт',
      steps: [
        ['Радіостанція експортує свій розклад', 'Вихідний формат може бути будь-яким: TXT, XML, CSV, HTML або власний формат ефірної системи.'],
        ['Ваш конвертер створює JSON Radiomonitor', 'Саме конвертер має зіставити локальні типи блоків і елементів зі стандартними кодами Radiomonitor.'],
        ['Готовий JSON перевіряється через Schedule JSON Test', 'Перевірка використовує ті самі правила валідації, що й реальний імпорт, але нічого не записує в базу.'],
        ['Кабінет або backend Radiomonitor виконує реальний імпорт', 'Якщо JSON валідний і дата ще не імпортована, елементи записуються в radio_schedule конкретної станції.']
      ]
    },
    authentication: {
      eyebrow: 'Безпека',
      title: 'Авторизація',
      p1: 'Schedule JSON Test використовує X-API-Key. API key видається для конкретної радіостанції та потрібен для перевірки JSON.',
      cardLabel: 'Обов’язкові headers',
      p2Before: 'Реальний імпорт через кабінет виконує backend Radiomonitor, тому користувач не вводить API key вручну. Поле',
      p2After: 'у JSON має відповідати вибраній радіостанції. Shortcode залишається внутрішньою сутністю Radiomonitor.'
    },
    jsonRule: {
      eyebrow: 'Правило формату',
      title: 'Radiomonitor приймає тільки готовий JSON',
      p: 'Radiomonitor не перетворює TXT, XML, CSV або інші файли у розклад і не намагається вгадати, де у вашому розкладі музика, реклама, новини або передача. Це має зробити ваш конвертер. API приймає тільки результат цієї роботи — готовий нормалізований JSON.',
      rejectTitle: 'Не приймається напряму',
      rejectText: 'TXT, XML, CSV, HTML, XLS, внутрішні формати ефірних систем, сирі вивантаження з плейауту.',
      acceptTitle: 'Приймається',
      acceptText: 'JSON, у якому блоки вже мають block_type/block_type_name, а кожен елемент має правильний elem_class.'
    },
    blockTypes: {
      eyebrow: 'Блочна модель',
      title: '4 системні типи блоків',
      p: 'Блок — це логічна частина ефіру. Назва блоку може бути будь-якою, але тип блоку має бути одним із чотирьох системних типів Radiomonitor.',
      rows: blockTypes
    },
    elementClasses: {
      eyebrow: 'Елементи',
      title: '5 системних класів елементів',
      p: 'Елемент — це конкретна одиниця всередині блоку: трек, джингл, рекламний ролик, передача або новина. Саме elem_class визначає, як Radiomonitor буде трактувати цей елемент у сервісах.',
      rows: elementClasses
    },
    mapping: {
      eyebrow: 'Маппінг',
      title: 'Як зіставити ваш розклад зі стандартом Radiomonitor',
      p: 'У різних радіостанцій структура розкладу може сильно відрізнятися. У вашому конвертері потрібно зробити явне зіставлення: локальний тип блоку → block_type/block_type_name, локальний тип елемента → elem_class.',
      rows: mappingRows,
      warningBefore: 'Важливо: використовуйте латинські коди. Наприклад, для новин потрібна латинська',
      warningAfter: ', а не кирилична'
    },
    structure: {
      eyebrow: 'JSON',
      title: 'Структура JSON',
      p: 'Верхній рівень описує Radiomonitor Radio ID, файл і дату розкладу. Усередині знаходиться масив blocks. Кожен блок містить системний тип і масив elements. Кожен елемент має час початку, час завершення, назву і клас.'
    },
    radioId: {
      eyebrow: 'Ідентифікація станції',
      title: 'Radiomonitor Radio ID',
      p1Before: '— це технічний ID радіостанції у Radiomonitor. Він не є секретом і надсилається користувачу разом з API key у листі',
      p1After: '. API key є секретом, а Radiomonitor Radio ID — ні, але він є обов’язковим захистом від переплутаного файлу.',
      p2Before: 'потрібно вставляти у кожен JSON-файл розкладу. Це захищає від випадкового імпорту розкладу не тієї радіостанції: API key і',
      p2After: 'мають належати одній радіостанції.'
    },
    time: {
      eyebrow: 'Ефірний день',
      title: 'Час і ефірний день',
      p1After: '— це дата ефірного дня. Один JSON описує один ефірний день однієї станції.',
      p2Before: 'і',
      p2After: 'передаються в локальному часі радіостанції. Дозволені формати часу:',
      p3: 'Стандартний день іде від 00:00:00 до 23:59:59. Якщо елемент перетинає північ, конвертер радіостанції має коректно підготувати розклад згідно з правилами одного ефірного дня. API перевіряє формат часу, але не вгадує ефірну логіку: відповідальність за правильну підготовку часу несе конвертер.'
    },
    fields: {
      eyebrow: 'Поля',
      title: 'Обов’язкові й додаткові поля',
      blockTitle: 'Поля блоку',
      elementTitle: 'Поля елемента'
    },
    example: {
      eyebrow: 'Приклад',
      title: 'Приклад JSON-запиту',
      p: 'У прикладі показані всі 4 типи блоків: Music, Commerce, News і Program. Блоки йдуть послідовно, без вкладеного рекламного блоку всередині музичного. Зверніть увагу, що джингли залишаються elem_class = J незалежно від того, у якому блоці вони стоять.'
    },
    response: {
      eyebrow: 'Приклад',
      title: 'Успішна відповідь',
      p: 'Успішна відповідь підтверджує, яку станцію, schema, дату, кількість блоків і кількість елементів було прийнято.'
    },
    errors: {
      eyebrow: 'Помилки',
      title: 'Помилки та валідація'
    },
    duplicates: {
      eyebrow: 'Історичність',
      title: 'Повторна дата не імпортується',
      p: 'Для однієї радіостанції одна playlist_date може бути імпортована тільки один раз. Якщо розклад за цю дату вже є, API поверне 409 Conflict і не додасть нові рядки. Якщо розклад за дату вже імпортовано, API не замінює його. Виправлення помилково завантаженого розкладу виконується тільки через окрему адміністративну процедуру або підтримку Radiomonitor.'
    },
    mistakes: {
      eyebrow: 'Контроль',
      title: 'Типові помилки під час підготовки JSON',
      items: [
        ['Кириличні коди', 'Передано Н або М замість латинських N або M.'],
        ['Неправильний block_type_name', 'Наприклад block_type = C, але block_type_name = Music.'],
        ['Рекламний ролик як J', 'Джингл має бути J, але сам рекламний ролик має бути C.'],
        ['Новини як B', 'Новинний елемент має бути N. B використовується для програм і передач.'],
        ['Кілька дат в одному JSON', 'Один JSON-файл має містити тільки одну playlist_date.'],
        ['Спроба повторного імпорту', 'Якщо дата вже завантажена, API поверне schedule_date_already_imported.']
      ]
    },
    support: {
      eyebrow: 'Підтримка',
      title: 'Підтримка',
      text: 'Якщо вам потрібно перевірити маппінг вашої ефірної системи або приклад JSON перед запуском, зверніться до Radiomonitor support:'
    },
    quick: {
      title: 'Швидка довідка',
      method: 'Метод',
      internalEndpoint: 'Internal endpoint',
      headers: 'Headers',
      publicTest: 'Public test',
      oneFile: 'Один файл',
      blockTypes: 'Block types',
      elementClasses: 'Element classes',
      link: 'Переглянути JSON-приклад'
    },
    test: {
      heroTitle: 'Radiomonitor Schedule JSON Test',
      heroText: 'Radiomonitor не перетворює TXT, XML, CSV або інші файли у розклад. Ваша система має сама підготувати готовий JSON за форматом Radiomonitor. Schedule JSON Test перевіряє, що JSON має правильний формат і що radio_id у файлі відповідає API key цієї радіостанції. Якщо radio_id не збігається, тест зупинить перевірку з помилкою radio_id_mismatch. Тест показує повний розклад у preview, нічого не імпортує, нічого не записує в базу і не перевіряє, чи дата вже була імпортована. Потрібні лише API key і JSON file.',
      apiKey: 'API key',
      jsonFile: 'JSON file',
      testButton: 'Test JSON',
      clearButton: 'Clear',
      terminalTitle: 'Preview terminal',
      idle: '[INFO] Виберіть JSON-файл і натисніть Test JSON.',
      missingApiKey: '[ERROR] missing_api_key\nmessage: Введіть API key перед перевіркою JSON.',
      missingFile: '[ERROR] missing_json_file\nmessage: Виберіть .json файл перед перевіркою.',
      invalidJson: '[ERROR] invalid_local_json',
      loading: '[INFO] Sending JSON to Radiomonitor preview API...',
      networkError: '[ERROR] network_error'
    }
  }
};

const scheduleLangOverrides = {
  en: {
    kicker: 'Radiomonitor developer docs',
    sidebarTitle: 'On this page',
    yes: 'yes',
    no: 'no',
    sidebar: [
      ['schedule-overview', 'Overview'],
      ['schedule-flow', 'How import works'],
      ['schedule-authentication', 'Authentication'],
      ['schedule-json-rule', 'Ready JSON only'],
      ['schedule-blocks', 'Block types'],
      ['schedule-elements', 'Element classes'],
      ['schedule-mapping', 'Mapping your codes'],
      ['schedule-structure', 'JSON structure'],
      ['schedule-radio-id', 'Radiomonitor Radio ID'],
      ['schedule-time', 'Time and broadcast day'],
      ['schedule-fields', 'Required fields'],
      ['schedule-example', 'JSON example'],
      ['schedule-response', 'Success response'],
      ['schedule-errors', 'Errors'],
      ['schedule-duplicates', 'Repeated date'],
      ['schedule-mistakes', 'Common mistakes'],
      ['schedule-support', 'Support']
    ],
    columns: {
      code: 'Code',
      name: 'Name',
      meaning: 'Meaning',
      usage: 'How to use',
      sourceName: 'How it may be named in your system',
      targetName: 'What to send to Radiomonitor',
      field: 'Field',
      type: 'Type',
      required: 'Required',
      description: 'Description'
    },
    hero: {
      text: 'Radiomonitor does not convert TXT, XML, CSV, or other files into a schedule. Your system must prepare ready-to-use JSON in the Radiomonitor format. Schedule JSON Test only validates this JSON and shows how Radiomonitor sees it. The test does not import anything and does not write anything to the database.'
    },
    overview: {
      eyebrow: 'Overview',
      title: 'What this API is for',
      p1: 'Schedule Import API is used to upload a ready daily broadcast schedule for a radio station. Unlike listener snapshots, schedules are not sent continuously. Usually this is one JSON file for one broadcast day.',
      p2: 'The import endpoint is a backend/internal integration endpoint. The station developer usually prepares JSON and checks it in the Schedule JSON Test tab. Shortcode is an internal Radiomonitor entity: the user does not need to know or enter it manually.',
      p3: 'To check a file, use Schedule JSON Test, where only an API key and a JSON file are required. The real import is performed by the Radiomonitor account area or backend.',
      tiles: [
        ['Input', 'Ready JSON', 'The schedule must already be converted to the Radiomonitor Schedule API standard.'],
        ['One file', 'One date', 'One JSON file must contain only one playlist_date for one radio station.'],
        ['Storage', 'Historical table', 'After import, data is accumulated in the station schema and is not replaced through the API.']
      ]
    },
    flow: {
      title: 'How import works',
      steps: [
        ['The radio station exports its schedule', 'The source format may be anything: TXT, XML, CSV, HTML, or a proprietary playout format.'],
        ['Your converter creates Radiomonitor JSON', 'The converter must map local block and element types to Radiomonitor standard codes.'],
        ['The ready JSON is checked with Schedule JSON Test', 'The test uses the same format validation rules as the real import, but writes nothing to the database.'],
        ['The Radiomonitor account area or backend performs the real import', 'If the JSON is valid and the date has not been imported yet, elements are written to the station radio_schedule table.']
      ]
    },
    authentication: {
      eyebrow: 'Security',
      title: 'Authentication',
      p1: 'Schedule JSON Test uses X-API-Key. The API key is issued for a specific radio station and is required to validate JSON.',
      cardLabel: 'Required headers',
      p2Before: 'The real import through the account area is performed by the Radiomonitor backend, so the user does not enter an API key manually. The',
      p2After: 'field in JSON must match the selected radio station. Shortcode remains an internal Radiomonitor entity.'
    },
    jsonRule: {
      eyebrow: 'Format rule',
      title: 'Radiomonitor accepts ready JSON only',
      p: 'Radiomonitor does not convert TXT, XML, CSV, or other files into a schedule and does not try to guess where music, advertising, news, or a program is located in your schedule. Your converter must do that. The API accepts only the result of this work: ready normalized JSON.',
      rejectTitle: 'Not accepted directly',
      rejectText: 'TXT, XML, CSV, HTML, XLS, internal playout formats, raw exports from an automation system.',
      acceptTitle: 'Accepted',
      acceptText: 'JSON where blocks already have block_type/block_type_name and each element has the correct elem_class.'
    },
    blockTypes: {
      eyebrow: 'Block model',
      title: '4 system block types',
      p: 'A block is a logical part of the broadcast. The block name can be anything, but the block type must be one of the four Radiomonitor system types.',
      rows: [
        ['M', 'Music', 'Music block', 'Use for a part of the broadcast whose main purpose is music. It may contain M tracks and J jingles.'],
        ['P', 'Program', 'Program / show', 'Use for programs, shows, segments, stories, author programs, or large program elements.'],
        ['C', 'Commerce', 'Advertising / commercial block', 'Use for an advertising block. The block length may be calculated from the first to the last element, but only C elements are treated as spots.'],
        ['N', 'News', 'News block', 'Use for news. Do not use the Cyrillic Н: the Latin N is required.']
      ]
    },
    elementClasses: {
      eyebrow: 'Elements',
      title: '5 system element classes',
      p: 'An element is a concrete item inside a block: a track, jingle, commercial spot, program, or news item. elem_class defines how Radiomonitor treats this item in services.',
      rows: [
        ['M', 'Music item', 'Music track', 'A song, composition, or music element. These elements are important for TOP and music reports.'],
        ['J', 'Jingle / separator', 'Jingle / opener / separator', 'Intro, outro, station liner, separator, or station jingle. It is not a track, ad, news item, or program.'],
        ['C', 'Commerce item', 'Commercial spot', 'The actual advertising or commercial item inside an advertising block.'],
        ['B', 'Program item', 'Program / large program item', 'The main part of a program, show, story, segment, or other program content.'],
        ['N', 'News item', 'News item', 'A news item, bulletin, or informational material inside a news block.']
      ]
    },
    mapping: {
      eyebrow: 'Mapping',
      title: 'How to map your schedule to the Radiomonitor standard',
      p: 'Schedule structures differ between radio stations. Your converter must explicitly map local block types to block_type/block_type_name and local element types to elem_class.',
      rows: mappingRows,
      warningBefore: 'Important: use Latin codes. For example, news must use Latin',
      warningAfter: ', not Cyrillic'
    },
    structure: {
      title: 'JSON structure',
      p: 'The top level describes Radiomonitor Radio ID, the file, and the schedule date. Inside it is the blocks array. Each block contains a system type and an elements array. Each element has a start time, end time, name, and class.'
    },
    radioId: {
      eyebrow: 'Station identification',
      p1Before: 'is the technical radio station ID in Radiomonitor. It is not a secret and is sent to the user together with the API key in the',
      p1After: 'email. The API key is secret, while Radiomonitor Radio ID is not, but it is required protection against a mixed-up file.',
      p2Before: 'must be included in every schedule JSON file. It protects against accidentally importing a schedule for the wrong station: the API key and',
      p2After: 'must belong to the same radio station.'
    },
    time: {
      eyebrow: 'Broadcast day',
      title: 'Time and broadcast day',
      p1After: 'is the broadcast day date. One JSON describes one broadcast day for one station.',
      p2Before: 'and',
      p2After: 'are sent in the station local time. Allowed time formats:',
      p3: 'A standard day runs from 00:00:00 to 23:59:59. If an element crosses midnight, the station converter must prepare the schedule correctly according to the one-broadcast-day rule. The API validates the time format but does not infer broadcast logic: the converter is responsible for preparing times correctly.'
    },
    fields: {
      eyebrow: 'Fields',
      title: 'Required and optional fields',
      blockTitle: 'Block fields',
      elementTitle: 'Element fields'
    },
    example: {
      eyebrow: 'Example',
      title: 'JSON request example',
      p: 'The example shows all 4 block types: Music, Commerce, News, and Program. Blocks are sequential, with no advertising block nested inside a music block. Jingles remain elem_class = J regardless of the block they appear in.'
    },
    response: {
      eyebrow: 'Example',
      title: 'Success response',
      p: 'A successful response confirms which station, schema, date, number of blocks, and number of elements were accepted.'
    },
    errors: {
      eyebrow: 'Errors',
      title: 'Errors and validation'
    },
    duplicates: {
      eyebrow: 'History',
      title: 'Repeated dates are not imported',
      p: 'For one radio station, one playlist_date can be imported only once. If the schedule for this date already exists, the API returns 409 Conflict and does not add new rows. If a schedule has already been imported for the date, the API does not replace it. Fixing an incorrectly uploaded schedule is done only through a separate administrative procedure or Radiomonitor support.'
    },
    mistakes: {
      eyebrow: 'Control',
      title: 'Common JSON preparation mistakes',
      items: [
        ['Cyrillic codes', 'Cyrillic Н or М was sent instead of Latin N or M.'],
        ['Wrong block_type_name', 'For example, block_type = C but block_type_name = Music.'],
        ['Commercial spot as J', 'A jingle must be J, but the actual commercial spot must be C.'],
        ['News as B', 'A news item must be N. B is used for programs and shows.'],
        ['Multiple dates in one JSON', 'One JSON file must contain only one playlist_date.'],
        ['Repeated import attempt', 'If the date has already been uploaded, the API returns schedule_date_already_imported.']
      ]
    },
    support: {
      eyebrow: 'Support',
      title: 'Support',
      text: 'If you need to check your playout-system mapping or a JSON example before launch, contact Radiomonitor support:'
    },
    quick: {
      title: 'Quick reference',
      method: 'Method',
      internalEndpoint: 'Internal endpoint',
      headers: 'Headers',
      publicTest: 'Public test',
      oneFile: 'One file',
      blockTypes: 'Block types',
      elementClasses: 'Element classes',
      link: 'View JSON example'
    },
    test: {
      heroTitle: 'Radiomonitor Schedule JSON Test',
      heroText: 'Radiomonitor does not convert TXT, XML, CSV, or other files into a schedule. Your system must prepare ready-to-use JSON in the Radiomonitor format. Schedule JSON Test checks that the JSON has the correct format and that the radio_id in the file matches the API key for this station. If radio_id does not match, the test stops with radio_id_mismatch. The test shows the full schedule in preview, imports nothing, writes nothing to the database, and does not check whether the date has already been imported. Only API key and JSON file are required.',
      apiKey: 'API key',
      jsonFile: 'JSON file',
      testButton: 'Test JSON',
      terminalTitle: 'Preview terminal',
      idle: '[INFO] Select a JSON file and click Test JSON.',
      missingApiKey: '[ERROR] missing_api_key\nmessage: Enter API key before testing JSON.',
      missingFile: '[ERROR] missing_json_file\nmessage: Choose a .json file before testing.',
      invalidJson: '[ERROR] invalid_local_json',
      loading: '[INFO] Sending JSON to Radiomonitor preview API...',
      networkError: '[ERROR] network_error',
      clearButton: 'Clear'
    }
  },
  it: {
    kicker: 'Documentazione per sviluppatori Radiomonitor',
    sidebarTitle: 'In questa pagina',
    yes: 'sì',
    no: 'no',
    sidebar: [
      ['schedule-overview', 'Panoramica'],
      ['schedule-flow', 'Come funziona l’importazione'],
      ['schedule-authentication', 'Autenticazione'],
      ['schedule-json-rule', 'Solo JSON pronto'],
      ['schedule-blocks', 'Tipi di blocco'],
      ['schedule-elements', 'Classi degli elementi'],
      ['schedule-mapping', 'Mappatura dei codici'],
      ['schedule-structure', 'Struttura JSON'],
      ['schedule-radio-id', 'Radiomonitor Radio ID'],
      ['schedule-time', 'Ora e giorno di trasmissione'],
      ['schedule-fields', 'Campi obbligatori'],
      ['schedule-example', 'Esempio JSON'],
      ['schedule-response', 'Risposta corretta'],
      ['schedule-errors', 'Errori'],
      ['schedule-duplicates', 'Data ripetuta'],
      ['schedule-mistakes', 'Errori comuni'],
      ['schedule-support', 'Supporto']
    ],
    columns: {
      code: 'Codice',
      name: 'Nome',
      meaning: 'Significato',
      usage: 'Come usarlo',
      sourceName: 'Come può chiamarsi nel vostro sistema',
      targetName: 'Cosa inviare a Radiomonitor',
      field: 'Campo',
      type: 'Tipo',
      required: 'Obbligatorio',
      description: 'Descrizione'
    },
    hero: {
      title: 'Radiomonitor Schedule Import API',
      text: 'Radiomonitor non converte TXT, XML, CSV o altri file in un palinsesto. Il vostro sistema deve preparare autonomamente un JSON pronto nel formato Radiomonitor. Schedule JSON Test verifica solo questo JSON e mostra come Radiomonitor lo interpreta. Il test non importa nulla e non scrive nulla nel database.'
    },
    overview: {
      eyebrow: 'Panoramica',
      title: 'A cosa serve questa API',
      p1: 'Schedule Import API serve a caricare un palinsesto giornaliero già pronto per una stazione radio. A differenza degli snapshot degli ascoltatori, il palinsesto non viene inviato continuamente. Di solito è un file JSON per un giorno di trasmissione.',
      p2: 'L’endpoint di importazione è un endpoint backend/internal integration. Di norma lo sviluppatore della radio prepara il JSON e lo controlla nella scheda Schedule JSON Test. Lo shortcode è un’entità interna di Radiomonitor: l’utente non deve conoscerlo né inserirlo manualmente.',
      p3: 'Per controllare un file, usate Schedule JSON Test: servono solo API key e file JSON. L’importazione reale viene eseguita dall’area account o dal backend Radiomonitor.',
      tiles: [
        ['Input', 'JSON pronto', 'Il palinsesto deve essere già convertito allo standard Radiomonitor Schedule API.'],
        ['Un file', 'Una data', 'Un file JSON deve contenere una sola playlist_date per una sola stazione radio.'],
        ['Salvataggio', 'Tabella storica', 'Dopo l’importazione, i dati vengono accumulati nello schema della stazione e non vengono sostituiti tramite API.']
      ]
    },
    flow: {
      eyebrow: 'Flow',
      title: 'Come funziona l’importazione',
      steps: [
        ['La radio esporta il proprio palinsesto', 'Il formato di origine può essere qualsiasi: TXT, XML, CSV, HTML o un formato proprietario del sistema di playout.'],
        ['Il vostro convertitore crea il JSON Radiomonitor', 'Il convertitore deve mappare i tipi locali di blocchi ed elementi sui codici standard Radiomonitor.'],
        ['Il JSON pronto viene controllato con Schedule JSON Test', 'Il test usa le stesse regole di validazione del formato dell’importazione reale, ma non scrive nulla nel database.'],
        ['L’area account o il backend Radiomonitor esegue l’importazione reale', 'Se il JSON è valido e la data non è ancora stata importata, gli elementi vengono scritti nella tabella radio_schedule della stazione.']
      ]
    },
    authentication: {
      eyebrow: 'Sicurezza',
      title: 'Autenticazione',
      p1: 'Schedule JSON Test usa X-API-Key. La API key viene emessa per una specifica stazione radio ed è necessaria per validare il JSON.',
      cardLabel: 'Header obbligatori',
      p2Before: 'L’importazione reale tramite l’area account viene eseguita dal backend Radiomonitor, quindi l’utente non inserisce manualmente la API key. Il campo',
      p2After: 'nel JSON deve corrispondere alla stazione radio selezionata. Lo shortcode rimane un’entità interna di Radiomonitor.'
    },
    jsonRule: {
      eyebrow: 'Regola del formato',
      title: 'Radiomonitor accetta solo JSON pronto',
      p: 'Radiomonitor non converte TXT, XML, CSV o altri file in un palinsesto e non prova a indovinare dove nel vostro palinsesto ci siano musica, pubblicità, notizie o programmi. Questo deve farlo il vostro convertitore. L’API accetta solo il risultato di questo lavoro: un JSON normalizzato e pronto.',
      rejectTitle: 'Non accettati direttamente',
      rejectText: 'TXT, XML, CSV, HTML, XLS, formati interni dei sistemi di playout, esportazioni grezze dall’automazione.',
      acceptTitle: 'Accettato',
      acceptText: 'JSON in cui i blocchi hanno già block_type/block_type_name e ogni elemento ha il corretto elem_class.'
    },
    blockTypes: {
      eyebrow: 'Modello a blocchi',
      title: '4 tipi di blocco di sistema',
      p: 'Un blocco è una parte logica della trasmissione. Il nome del blocco può essere libero, ma il tipo deve essere uno dei quattro tipi di sistema Radiomonitor.',
      rows: [
        ['M', 'Music', 'Blocco musicale', 'Usare per una parte della trasmissione il cui scopo principale è la musica. Può contenere brani M e jingle J.'],
        ['P', 'Program', 'Programma / show', 'Usare per programmi, show, rubriche, racconti, programmi d’autore o grandi elementi di programma.'],
        ['C', 'Commerce', 'Blocco pubblicitario / commerciale', 'Usare per un blocco pubblicitario. La durata del blocco può essere calcolata dal primo all’ultimo elemento, ma solo gli elementi C sono considerati spot.'],
        ['N', 'News', 'Blocco notizie', 'Usare per le notizie. Non usare la Н cirillica: serve la N latina.']
      ]
    },
    elementClasses: {
      eyebrow: 'Elementi',
      title: '5 classi di elementi di sistema',
      p: 'Un elemento è una singola unità dentro un blocco: brano, jingle, spot pubblicitario, programma o notizia. elem_class determina come Radiomonitor interpreterà l’elemento nei servizi.',
      rows: [
        ['M', 'Music item', 'Brano musicale', 'Canzone, composizione o elemento musicale. Questi elementi sono importanti per TOP e report musicali.'],
        ['J', 'Jingle / separator', 'Jingle / sigla / separatore', 'Intro, outro, sigla, stacco, jingle di stazione. Non è un brano, una pubblicità, una notizia o un programma.'],
        ['C', 'Commerce item', 'Spot pubblicitario', 'L’elemento pubblicitario o commerciale vero e proprio dentro un blocco pubblicitario.'],
        ['B', 'Program item', 'Programma / grande elemento di programma', 'La parte principale di un programma, show, racconto, rubrica o altro contenuto di programma.'],
        ['N', 'News item', 'Elemento notizie', 'Notizia, notiziario o materiale informativo dentro un blocco notizie.']
      ]
    },
    mapping: {
      eyebrow: 'Mappatura',
      title: 'Come mappare il vostro palinsesto sullo standard Radiomonitor',
      p: 'La struttura del palinsesto può variare molto tra le radio. Nel vostro convertitore bisogna definire una mappatura esplicita: tipo locale di blocco → block_type/block_type_name, tipo locale di elemento → elem_class.',
      rows: mappingRows,
      warningBefore: 'Importante: usate codici latini. Per esempio, per le notizie serve la',
      warningAfter: 'latina, non quella cirillica'
    },
    structure: {
      eyebrow: 'JSON',
      title: 'Struttura JSON',
      p: 'Il livello principale descrive Radiomonitor Radio ID, file e data del palinsesto. All’interno c’è l’array blocks. Ogni blocco contiene un tipo di sistema e un array elements. Ogni elemento ha ora di inizio, ora di fine, nome e classe.'
    },
    radioId: {
      eyebrow: 'Identificazione della stazione',
      title: 'Radiomonitor Radio ID',
      p1Before: 'è l’ID tecnico della stazione radio in Radiomonitor. Non è un segreto e viene inviato all’utente insieme alla API key nella email',
      p1After: '. La API key è segreta, mentre Radiomonitor Radio ID non lo è, ma è una protezione obbligatoria contro file scambiati per errore.',
      p2Before: 'deve essere inserito in ogni file JSON del palinsesto. Protegge dall’importazione accidentale del palinsesto di un’altra stazione: API key e',
      p2After: 'devono appartenere alla stessa stazione radio.'
    },
    time: {
      eyebrow: 'Giorno di trasmissione',
      title: 'Ora e giorno di trasmissione',
      p1After: 'è la data del giorno di trasmissione. Un JSON descrive un solo giorno di trasmissione di una sola stazione.',
      p2Before: 'e',
      p2After: 'vengono inviati nell’ora locale della stazione. Formati consentiti:',
      p3: 'Un giorno standard va da 00:00:00 a 23:59:59. Se un elemento attraversa la mezzanotte, il convertitore della radio deve preparare correttamente il palinsesto secondo la regola di un solo giorno di trasmissione. L’API valida il formato dell’ora, ma non interpreta la logica editoriale: la preparazione corretta degli orari è responsabilità del convertitore.'
    },
    fields: {
      eyebrow: 'Campi',
      title: 'Campi obbligatori e opzionali',
      blockTitle: 'Campi del blocco',
      elementTitle: 'Campi dell’elemento'
    },
    example: {
      eyebrow: 'Esempio',
      title: 'Esempio di richiesta JSON',
      p: 'L’esempio mostra tutti e 4 i tipi di blocco: Music, Commerce, News e Program. I blocchi sono sequenziali, senza un blocco pubblicitario annidato dentro un blocco musicale. I jingle restano elem_class = J indipendentemente dal blocco in cui si trovano.'
    },
    response: {
      eyebrow: 'Esempio',
      title: 'Risposta corretta',
      p: 'Una risposta corretta conferma stazione, schema, data, numero di blocchi e numero di elementi accettati.'
    },
    errors: {
      eyebrow: 'Errori',
      title: 'Errori e validazione'
    },
    duplicates: {
      eyebrow: 'Storico',
      title: 'Una data ripetuta non viene importata',
      p: 'Per una stazione radio, una playlist_date può essere importata una sola volta. Se il palinsesto per questa data esiste già, l’API restituisce 409 Conflict e non aggiunge nuove righe. Se un palinsesto per la data è già stato importato, l’API non lo sostituisce. La correzione di un palinsesto caricato per errore avviene solo tramite una procedura amministrativa separata o il supporto Radiomonitor.'
    },
    mistakes: {
      eyebrow: 'Controllo',
      title: 'Errori comuni nella preparazione del JSON',
      items: [
        ['Codici cirillici', 'È stata inviata Н o М cirillica invece di N o M latina.'],
        ['block_type_name errato', 'Per esempio block_type = C, ma block_type_name = Music.'],
        ['Spot pubblicitario come J', 'Un jingle deve essere J, ma lo spot pubblicitario vero e proprio deve essere C.'],
        ['Notizie come B', 'Un elemento notizie deve essere N. B viene usato per programmi e show.'],
        ['Più date in un JSON', 'Un file JSON deve contenere una sola playlist_date.'],
        ['Tentativo di importazione ripetuta', 'Se la data è già stata caricata, l’API restituisce schedule_date_already_imported.']
      ]
    },
    support: {
      eyebrow: 'Supporto',
      title: 'Supporto',
      text: 'Se dovete verificare la mappatura del vostro sistema di playout o un esempio JSON prima dell’avvio, contattate il supporto Radiomonitor:'
    },
    quick: {
      title: 'Riferimento rapido',
      method: 'Metodo',
      internalEndpoint: 'Endpoint interno',
      headers: 'Header',
      publicTest: 'Test pubblico',
      oneFile: 'Un file',
      blockTypes: 'Tipi di blocco',
      elementClasses: 'Classi degli elementi',
      link: 'Vedi esempio JSON'
    },
    test: {
      heroTitle: 'Radiomonitor Schedule JSON Test',
      heroText: 'Radiomonitor non converte TXT, XML, CSV o altri file in un palinsesto. Il vostro sistema deve preparare autonomamente un JSON pronto nel formato Radiomonitor. Schedule JSON Test verifica che il JSON abbia il formato corretto e che il radio_id nel file corrisponda alla API key di questa stazione. Se radio_id non corrisponde, il test si interrompe con radio_id_mismatch. Il test mostra il palinsesto completo in preview, non importa nulla, non scrive nulla nel database e non controlla se la data sia già stata importata. Servono solo API key e file JSON.',
      apiKey: 'API key',
      jsonFile: 'File JSON',
      testButton: 'Test JSON',
      clearButton: 'Cancella',
      terminalTitle: 'Terminale preview',
      idle: '[INFO] Selezionate un file JSON e premete Test JSON.',
      missingApiKey: '[ERROR] missing_api_key\nmessage: Inserite la API key prima di testare il JSON.',
      missingFile: '[ERROR] missing_json_file\nmessage: Selezionate un file .json prima del test.',
      invalidJson: '[ERROR] invalid_local_json',
      loading: '[INFO] Sending JSON to Radiomonitor preview API...',
      networkError: '[ERROR] network_error'
    }
  },
  fr: {
    kicker: 'Documentation développeur Radiomonitor',
    sidebarTitle: 'Sur cette page',
    yes: 'oui',
    no: 'non',
    sidebar: [
      ['schedule-overview', 'Vue d’ensemble'],
      ['schedule-flow', 'Fonctionnement de l’import'],
      ['schedule-authentication', 'Authentification'],
      ['schedule-json-rule', 'JSON prêt uniquement'],
      ['schedule-blocks', 'Types de blocs'],
      ['schedule-elements', 'Classes des éléments'],
      ['schedule-mapping', 'Mapping des codes'],
      ['schedule-structure', 'Structure JSON'],
      ['schedule-radio-id', 'Radiomonitor Radio ID'],
      ['schedule-time', 'Heure et jour d’antenne'],
      ['schedule-fields', 'Champs obligatoires'],
      ['schedule-example', 'Exemple JSON'],
      ['schedule-response', 'Réponse correcte'],
      ['schedule-errors', 'Erreurs'],
      ['schedule-duplicates', 'Date répétée'],
      ['schedule-mistakes', 'Erreurs fréquentes'],
      ['schedule-support', 'Support']
    ],
    columns: {
      code: 'Code',
      name: 'Nom',
      meaning: 'Signification',
      usage: 'Utilisation',
      sourceName: 'Nom possible dans votre système',
      targetName: 'Ce qu’il faut envoyer à Radiomonitor',
      field: 'Champ',
      type: 'Type',
      required: 'Obligatoire',
      description: 'Description'
    },
    hero: {
      title: 'Radiomonitor Schedule Import API',
      text: 'Radiomonitor ne convertit pas les fichiers TXT, XML, CSV ou autres en planning. Votre système doit préparer lui-même un JSON prêt au format Radiomonitor. Schedule JSON Test valide uniquement ce JSON et montre comment Radiomonitor l’interprète. Le test n’importe rien et n’écrit rien dans la base.'
    },
    overview: {
      eyebrow: 'Vue d’ensemble',
      title: 'À quoi sert cette API',
      p1: 'Schedule Import API sert à charger un planning quotidien déjà prêt pour une station radio. Contrairement aux snapshots d’auditeurs, le planning n’est pas envoyé en continu. Il s’agit généralement d’un fichier JSON pour un jour d’antenne.',
      p2: 'L’endpoint d’import est un endpoint backend/internal integration. En général, le développeur de la radio prépare le JSON et le vérifie dans l’onglet Schedule JSON Test. Le shortcode est une entité interne de Radiomonitor: l’utilisateur n’a pas à le connaître ni à le saisir manuellement.',
      p3: 'Pour vérifier un fichier, utilisez Schedule JSON Test: seuls l’API key et le fichier JSON sont nécessaires. L’import réel est exécuté par l’espace client ou par le backend Radiomonitor.',
      tiles: [
        ['Entrée', 'JSON prêt', 'Le planning doit déjà être converti au standard Radiomonitor Schedule API.'],
        ['Un fichier', 'Une date', 'Un fichier JSON doit contenir une seule playlist_date pour une seule station radio.'],
        ['Stockage', 'Table historique', 'Après import, les données s’accumulent dans le schema de la station et ne sont pas remplacées via l’API.']
      ]
    },
    flow: {
      eyebrow: 'Flow',
      title: 'Fonctionnement de l’import',
      steps: [
        ['La radio exporte son planning', 'Le format source peut être quelconque: TXT, XML, CSV, HTML ou format propriétaire du système de playout.'],
        ['Votre convertisseur crée le JSON Radiomonitor', 'Le convertisseur doit mapper les types locaux de blocs et d’éléments vers les codes standard Radiomonitor.'],
        ['Le JSON prêt est vérifié avec Schedule JSON Test', 'Le test utilise les mêmes règles de validation du format que l’import réel, mais n’écrit rien dans la base.'],
        ['L’espace client ou le backend Radiomonitor exécute l’import réel', 'Si le JSON est valide et que la date n’a pas encore été importée, les éléments sont écrits dans la table radio_schedule de la station.']
      ]
    },
    authentication: {
      eyebrow: 'Sécurité',
      title: 'Authentification',
      p1: 'Schedule JSON Test utilise X-API-Key. L’API key est émise pour une station radio précise et sert à valider le JSON.',
      cardLabel: 'Headers obligatoires',
      p2Before: 'L’import réel via l’espace client est exécuté par le backend Radiomonitor, donc l’utilisateur ne saisit pas l’API key manuellement. Le champ',
      p2After: 'dans le JSON doit correspondre à la station radio sélectionnée. Le shortcode reste une entité interne de Radiomonitor.'
    },
    jsonRule: {
      eyebrow: 'Règle de format',
      title: 'Radiomonitor accepte uniquement un JSON prêt',
      p: 'Radiomonitor ne convertit pas les fichiers TXT, XML, CSV ou autres en planning et n’essaie pas de deviner où se trouvent la musique, la publicité, les infos ou les programmes dans votre planning. C’est le rôle de votre convertisseur. L’API accepte seulement le résultat de ce travail: un JSON normalisé et prêt.',
      rejectTitle: 'Non acceptés directement',
      rejectText: 'TXT, XML, CSV, HTML, XLS, formats internes des systèmes de playout, exports bruts de l’automation.',
      acceptTitle: 'Accepté',
      acceptText: 'JSON où les blocs possèdent déjà block_type/block_type_name et où chaque élément possède le bon elem_class.'
    },
    blockTypes: {
      eyebrow: 'Modèle de blocs',
      title: '4 types de blocs système',
      p: 'Un bloc est une partie logique de l’antenne. Le nom du bloc peut être libre, mais son type doit être l’un des quatre types système Radiomonitor.',
      rows: [
        ['M', 'Music', 'Bloc musical', 'À utiliser pour une partie de l’antenne dont l’objectif principal est la musique. Peut contenir des titres M et des jingles J.'],
        ['P', 'Program', 'Programme / émission / show', 'À utiliser pour les émissions, shows, rubriques, histoires, programmes d’auteur ou grands éléments de programme.'],
        ['C', 'Commerce', 'Bloc publicitaire / commercial', 'À utiliser pour un bloc publicitaire. La durée du bloc peut être calculée du premier au dernier élément, mais seuls les éléments C sont traités comme des spots.'],
        ['N', 'News', 'Bloc infos', 'À utiliser pour les infos. N’utilisez pas le Н cyrillique: il faut la lettre latine N.']
      ]
    },
    elementClasses: {
      eyebrow: 'Éléments',
      title: '5 classes système d’éléments',
      p: 'Un élément est une unité concrète dans un bloc: titre musical, jingle, spot publicitaire, programme ou info. elem_class détermine comment Radiomonitor interprète cet élément dans ses services.',
      rows: [
        ['M', 'Music item', 'Titre musical', 'Chanson, composition ou élément musical. Ces éléments sont importants pour les TOP et les rapports musicaux.'],
        ['J', 'Jingle / separator', 'Jingle / habillage / séparateur', 'Intro, outro, habillage, séparateur, jingle de station. Ce n’est ni un titre, ni une publicité, ni une info, ni un programme.'],
        ['C', 'Commerce item', 'Spot publicitaire', 'L’élément publicitaire ou commercial proprement dit dans un bloc publicitaire.'],
        ['B', 'Program item', 'Programme / grand élément de programme', 'La partie principale d’une émission, d’un show, d’une histoire, d’une rubrique ou d’un autre contenu de programme.'],
        ['N', 'News item', 'Élément infos', 'Info, bulletin d’informations ou contenu informatif dans un bloc infos.']
      ]
    },
    mapping: {
      eyebrow: 'Mapping',
      title: 'Comment mapper votre planning vers le standard Radiomonitor',
      p: 'La structure du planning peut varier fortement d’une radio à l’autre. Dans votre convertisseur, il faut définir un mapping explicite: type local de bloc → block_type/block_type_name, type local d’élément → elem_class.',
      rows: mappingRows,
      warningBefore: 'Important: utilisez des codes latins. Par exemple, pour les infos il faut la lettre latine',
      warningAfter: ', pas la lettre cyrillique'
    },
    structure: {
      eyebrow: 'JSON',
      title: 'Structure JSON',
      p: 'Le niveau principal décrit le Radiomonitor Radio ID, le fichier et la date du planning. Il contient ensuite le tableau blocks. Chaque bloc contient un type système et un tableau elements. Chaque élément possède une heure de début, une heure de fin, un nom et une classe.'
    },
    radioId: {
      eyebrow: 'Identification de la station',
      title: 'Radiomonitor Radio ID',
      p1Before: 'est l’ID technique de la station radio dans Radiomonitor. Ce n’est pas un secret et il est envoyé à l’utilisateur avec l’API key dans l’email',
      p1After: '. L’API key est secrète, tandis que le Radiomonitor Radio ID ne l’est pas, mais il constitue une protection obligatoire contre les fichiers mélangés par erreur.',
      p2Before: 'doit être inséré dans chaque fichier JSON de planning. Il évite l’import accidentel du planning d’une autre station: l’API key et',
      p2After: 'doivent appartenir à la même station radio.'
    },
    time: {
      eyebrow: 'Jour d’antenne',
      title: 'Heure et jour d’antenne',
      p1After: 'est la date du jour d’antenne. Un JSON décrit un seul jour d’antenne pour une seule station.',
      p2Before: 'et',
      p2After: 'sont envoyés dans l’heure locale de la station. Formats autorisés:',
      p3: 'Une journée standard va de 00:00:00 à 23:59:59. Si un élément traverse minuit, le convertisseur de la radio doit préparer correctement le planning selon la règle d’un seul jour d’antenne. L’API valide le format de l’heure, mais ne devine pas la logique d’antenne: la préparation correcte des heures relève du convertisseur.'
    },
    fields: {
      eyebrow: 'Champs',
      title: 'Champs obligatoires et optionnels',
      blockTitle: 'Champs du bloc',
      elementTitle: 'Champs de l’élément'
    },
    example: {
      eyebrow: 'Exemple',
      title: 'Exemple de requête JSON',
      p: 'L’exemple montre les 4 types de blocs: Music, Commerce, News et Program. Les blocs sont séquentiels, sans bloc publicitaire imbriqué dans un bloc musical. Les jingles restent elem_class = J quel que soit le bloc où ils apparaissent.'
    },
    response: {
      eyebrow: 'Exemple',
      title: 'Réponse correcte',
      p: 'Une réponse correcte confirme la station, le schema, la date, le nombre de blocs et le nombre d’éléments acceptés.'
    },
    errors: {
      eyebrow: 'Erreurs',
      title: 'Erreurs et validation'
    },
    duplicates: {
      eyebrow: 'Historique',
      title: 'Une date répétée n’est pas importée',
      p: 'Pour une station radio, une playlist_date ne peut être importée qu’une seule fois. Si le planning pour cette date existe déjà, l’API renvoie 409 Conflict et n’ajoute aucune nouvelle ligne. Si un planning a déjà été importé pour cette date, l’API ne le remplace pas. La correction d’un planning chargé par erreur se fait uniquement via une procédure administrative distincte ou le support Radiomonitor.'
    },
    mistakes: {
      eyebrow: 'Contrôle',
      title: 'Erreurs fréquentes lors de la préparation du JSON',
      items: [
        ['Codes cyrilliques', 'Н ou М cyrillique a été envoyé au lieu de N ou M latin.'],
        ['block_type_name incorrect', 'Par exemple block_type = C, mais block_type_name = Music.'],
        ['Spot publicitaire en J', 'Un jingle doit être J, mais le spot publicitaire lui-même doit être C.'],
        ['Infos en B', 'Un élément info doit être N. B est utilisé pour les programmes et émissions.'],
        ['Plusieurs dates dans un JSON', 'Un fichier JSON doit contenir une seule playlist_date.'],
        ['Tentative d’import répété', 'Si la date a déjà été chargée, l’API renvoie schedule_date_already_imported.']
      ]
    },
    support: {
      eyebrow: 'Support',
      title: 'Support',
      text: 'Si vous devez vérifier le mapping de votre système de playout ou un exemple JSON avant le lancement, contactez le support Radiomonitor:'
    },
    quick: {
      title: 'Référence rapide',
      method: 'Méthode',
      internalEndpoint: 'Endpoint interne',
      headers: 'Headers',
      publicTest: 'Test public',
      oneFile: 'Un fichier',
      blockTypes: 'Types de blocs',
      elementClasses: 'Classes des éléments',
      link: 'Voir l’exemple JSON'
    },
    test: {
      heroTitle: 'Radiomonitor Schedule JSON Test',
      heroText: 'Radiomonitor ne convertit pas les fichiers TXT, XML, CSV ou autres en planning. Votre système doit préparer lui-même un JSON prêt au format Radiomonitor. Schedule JSON Test vérifie que le JSON a le bon format et que le radio_id du fichier correspond à l’API key de cette station. Si radio_id ne correspond pas, le test s’arrête avec radio_id_mismatch. Le test affiche le planning complet en preview, n’importe rien, n’écrit rien dans la base et ne vérifie pas si la date a déjà été importée. Seuls l’API key et le fichier JSON sont nécessaires.',
      apiKey: 'API key',
      jsonFile: 'Fichier JSON',
      testButton: 'Tester JSON',
      clearButton: 'Effacer',
      terminalTitle: 'Terminal de preview',
      idle: '[INFO] Sélectionnez un fichier JSON et cliquez sur Test JSON.',
      missingApiKey: '[ERROR] missing_api_key\nmessage: Saisissez l’API key avant de tester le JSON.',
      missingFile: '[ERROR] missing_json_file\nmessage: Sélectionnez un fichier .json avant le test.',
      invalidJson: '[ERROR] invalid_local_json',
      loading: '[INFO] Sending JSON to Radiomonitor preview API...',
      networkError: '[ERROR] network_error'
    }
  }
};

function deepMerge(base, override) {
  const result = { ...base };
  Object.entries(override || {}).forEach(([key, value]) => {
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      base?.[key] &&
      typeof base[key] === 'object' &&
      !Array.isArray(base[key])
    ) {
      result[key] = deepMerge(base[key], value);
    } else {
      result[key] = value;
    }
  });
  return result;
}

function getScheduleCopy(lang) {
  const fieldSet = scheduleFieldSets[lang] || scheduleFieldSets.uk;
  return {
    ...deepMerge(scheduleTranslations.uk, scheduleLangOverrides[lang] || {}),
    topLevelFields: fieldSet.topLevel,
    blockFields: fieldSet.block,
    elementFields: fieldSet.element,
    blockExampleName: fieldSet.blockExampleName
  };
}

function FieldTable({ columns, rows }) {
  return (
    <div className="api-table-wrap">
      <table className="api-fields-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.join('-')}>
              {row.map((cell, index) => (
                <td key={`${row[0]}-${index}`}>{index === 0 ? <code>{cell}</code> : cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IngestApiContent({ copy }) {
  return (
    <>
      <header className="api-hero">
        <div>
          <div className="api-kicker">{copy.heroKicker}</div>
          <h1>{copy.heroTitle}</h1>
          <p>{copy.heroText}</p>
        </div>
        <div className="api-hero-panel" aria-label="Primary endpoint">
          <span className="api-method api-method-post">POST</span>
          <code>{endpointUrl}</code>
        </div>
      </header>

      <div className="api-doc-layout">
        <aside className="api-sidebar" aria-label="API documentation sections">
          <div className="api-sidebar-title">{copy.sidebarTitle}</div>
          <nav>
            {copy.sidebar.map(([id, label]) => (
              <a key={id} href={`#${id}`}>
                {label}
              </a>
            ))}
          </nav>
        </aside>

        <main className="api-doc-main">
          <Section id="overview" eyebrow={copy.overview.eyebrow} title={copy.overview.title}>
            <p>{copy.overview.text}</p>
            <div className="api-info-grid">
              {copy.overview.tiles.map(([label, title, text]) => (
                <div className="api-info-tile" key={label}>
                  <span>{label}</span>
                  <strong>{title}</strong>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section id="connection" eyebrow={copy.connection.eyebrow} title={copy.connection.title}>
            <ol className="api-step-list">
              {copy.connection.steps.map(([title, text], index) => (
                <li key={title}>
                  <span>{index + 1}</span>
                  <div>
                    <strong>{title}</strong>
                    <p>{text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Section>

          <Section id="authentication" eyebrow={copy.authentication.eyebrow} title={copy.authentication.title}>
            <p>
              {copy.authentication.textBefore}
              <code className="api-inline-code"> X-API-Key </code>
              {copy.authentication.textAfter}
              <code className="api-inline-code"> Content-Type: application/json</code>.
            </p>
            <div className="api-header-card">
              <div>
                <span>{copy.authentication.cardLabel}</span>
                <strong>X-API-Key</strong>
              </div>
              <div>
                <code>&lt;your-api-key&gt;</code>
                <code>Content-Type: application/json</code>
              </div>
            </div>
          </Section>

          <Section id="endpoint" eyebrow={copy.endpoint.eyebrow} title={copy.endpoint.title}>
            <div className="api-endpoint-row">
              <span className="api-method api-method-post">POST</span>
              <code>{endpointUrl}</code>
            </div>
            <p>{copy.endpoint.text}</p>
          </Section>

          <Section id="station-key" eyebrow={copy.stationKey.eyebrow} title={copy.stationKey.title}>
            <p>
              {copy.stationKey.text1Before}
              <code className="api-inline-code"> stream_id </code>
              {copy.stationKey.text1After}
            </p>
            <p>{copy.stationKey.text2}</p>
          </Section>

          <Section id="request-body" eyebrow={copy.requestBody.eyebrow} title={copy.requestBody.title}>
            <div className="api-table-wrap">
              <table className="api-fields-table">
                <thead>
                  <tr>
                    {copy.requestBody.columns.map((column) => (
                      <th key={column}>{column}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {requestFieldOrder.map(([field, type, required]) => (
                    <tr key={field}>
                      <td><code>{field}</code></td>
                      <td>{type}</td>
                      <td>{required ? copy.requestBody.yes : copy.requestBody.no}</td>
                      <td>{copy.requestBody.descriptions[field]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section id="request-example" eyebrow={copy.examples.requestEyebrow} title={copy.examples.requestTitle}>
            <CodeBlock>{requestExample}</CodeBlock>
          </Section>

          <Section id="response-example" eyebrow={copy.examples.responseEyebrow} title={copy.examples.responseTitle}>
            <CodeBlock>{responseExample}</CodeBlock>
          </Section>

          <Section id="errors" eyebrow={copy.errors.eyebrow} title={copy.errors.title}>
            <div className="api-error-grid">
              {errorCodes.map(([status, code]) => (
                <div key={`${status}-${code}`}>
                  <strong>{status}</strong>
                  <span>{code}</span>
                </div>
              ))}
            </div>
            <CodeBlock>{errorExample}</CodeBlock>
          </Section>

          <Section id="sender-ip" eyebrow={copy.senderIp.eyebrow} title={copy.senderIp.title}>
            <p>{copy.senderIp.p1}</p>
            <p>{copy.senderIp.p2}</p>
          </Section>

          <Section id="direct-vs-sender" eyebrow={copy.directVsSender.eyebrow} title={copy.directVsSender.title}>
            <div className="api-compare">
              <div>
                <h3>{copy.directVsSender.directTitle}</h3>
                <p>{copy.directVsSender.directText}</p>
              </div>
              <div>
                <h3>{copy.directVsSender.senderTitle}</h3>
                <p>{copy.directVsSender.senderText}</p>
              </div>
            </div>
          </Section>

          <Section id="support" eyebrow={copy.support.eyebrow} title={copy.support.title}>
            <p>
              {copy.support.text}
              <a href="mailto:support@radiomonitor.net">support@radiomonitor.net</a>.
            </p>
          </Section>
        </main>

        <aside className="api-reference-panel" aria-label="API quick reference">
          <div className="api-reference-card">
            <div className="api-reference-title">{copy.quick.title}</div>
            <div className="api-reference-item">
              <span>{copy.quick.method}</span>
              <strong>POST</strong>
            </div>
            <div className="api-reference-item">
              <span>{copy.quick.endpoint}</span>
              <code>{endpointUrl}</code>
            </div>
            <div className="api-reference-item">
              <span>{copy.quick.headers}</span>
              <code>X-API-Key: &lt;your-api-key&gt;</code>
              <code>Content-Type: application/json</code>
            </div>
            <a className="api-reference-link" href="#request-example">
              {copy.quick.link}
            </a>
          </div>
        </aside>
      </div>
    </>
  );
}

function ScheduleApiContent({ copy }) {
  return (
    <>
      <header className="api-hero">
        <div>
          <div className="api-kicker">{copy.kicker}</div>
          <h1>{copy.hero.title}</h1>
          <p>{copy.hero.text}</p>
        </div>
        <div className="api-hero-panel" aria-label="Schedule import endpoint">
          <span className="api-method api-method-post">POST</span>
          <code>{scheduleEndpointUrl}</code>
        </div>
      </header>

      <div className="api-doc-layout">
        <aside className="api-sidebar" aria-label="Schedule API documentation sections">
          <div className="api-sidebar-title">{copy.sidebarTitle}</div>
          <nav>
            {copy.sidebar.map(([id, label]) => (
              <a key={id} href={`#${id}`}>
                {label}
              </a>
            ))}
          </nav>
        </aside>

        <main className="api-doc-main">
          <Section id="schedule-overview" eyebrow={copy.overview.eyebrow} title={copy.overview.title}>
            <p>{copy.overview.p1}</p>
            <p>{copy.overview.p2}</p>
            <p>{copy.overview.p3}</p>
            <div className="api-info-grid">
              {copy.overview.tiles.map(([label, title, text]) => (
                <div className="api-info-tile" key={label}>
                  <span>{label}</span>
                  <strong>{title}</strong>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section id="schedule-flow" eyebrow={copy.flow.eyebrow} title={copy.flow.title}>
            <ol className="api-step-list">
              {copy.flow.steps.map(([title, text], index) => (
                <li key={title}>
                  <span>{index + 1}</span>
                  <div>
                    <strong>{title}</strong>
                    <p>{text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Section>

          <Section id="schedule-authentication" eyebrow={copy.authentication.eyebrow} title={copy.authentication.title}>
            <p>{copy.authentication.p1}</p>
            <div className="api-header-card">
              <div>
                <span>{copy.authentication.cardLabel}</span>
                <strong>X-API-Key</strong>
              </div>
              <div>
                <code>X-API-Key: &lt;your-api-key&gt;</code>
                <code>Content-Type: application/json</code>
              </div>
            </div>
            <p>
              {copy.authentication.p2Before}
              <code className="api-inline-code"> radio_id </code>
              {copy.authentication.p2After}
            </p>
          </Section>

          <Section id="schedule-json-rule" eyebrow={copy.jsonRule.eyebrow} title={copy.jsonRule.title}>
            <p>{copy.jsonRule.p}</p>
            <div className="api-compare">
              <div>
                <h3>{copy.jsonRule.rejectTitle}</h3>
                <p>{copy.jsonRule.rejectText}</p>
              </div>
              <div>
                <h3>{copy.jsonRule.acceptTitle}</h3>
                <p>{copy.jsonRule.acceptText}</p>
              </div>
            </div>
          </Section>

          <Section id="schedule-blocks" eyebrow={copy.blockTypes.eyebrow} title={copy.blockTypes.title}>
            <p>{copy.blockTypes.p}</p>
            <FieldTable
              columns={[copy.columns.code, copy.columns.name, copy.columns.meaning, copy.columns.usage]}
              rows={copy.blockTypes.rows}
            />
            <CodeBlock>{`{
  "block_name": "${copy.blockExampleName}",
  "block_type": "N",
  "block_type_name": "News"
}`}</CodeBlock>
          </Section>

          <Section id="schedule-elements" eyebrow={copy.elementClasses.eyebrow} title={copy.elementClasses.title}>
            <p>{copy.elementClasses.p}</p>
            <FieldTable
              columns={[copy.columns.code, copy.columns.name, copy.columns.meaning, copy.columns.usage]}
              rows={copy.elementClasses.rows}
            />
          </Section>

          <Section id="schedule-mapping" eyebrow={copy.mapping.eyebrow} title={copy.mapping.title}>
            <p>{copy.mapping.p}</p>
            <FieldTable
              columns={[copy.columns.sourceName, copy.columns.targetName]}
              rows={copy.mapping.rows}
            />
            <p>
              {copy.mapping.warningBefore}
              <code className="api-inline-code"> N </code>
              {copy.mapping.warningAfter}
              <code className="api-inline-code"> Н </code>
              .
            </p>
          </Section>

          <Section id="schedule-structure" eyebrow={copy.structure.eyebrow} title={copy.structure.title}>
            <p>{copy.structure.p}</p>
            <FieldTable
              columns={[copy.columns.field, copy.columns.type, copy.columns.required, copy.columns.description]}
              rows={copy.topLevelFields.map(([field, type, required, description]) => [field, type, required ? copy.yes : copy.no, description])}
            />
          </Section>

          <Section id="schedule-radio-id" eyebrow={copy.radioId.eyebrow} title={copy.radioId.title}>
            <p>
              <code className="api-inline-code"> radio_id </code>
              {copy.radioId.p1Before}
              <code className="api-inline-code"> stat.provision_ready</code>.
              {copy.radioId.p1After}
            </p>
            <p>
              <code className="api-inline-code"> radio_id </code>
              {copy.radioId.p2Before}
              <code className="api-inline-code"> radio_id </code>
              {copy.radioId.p2After}
            </p>
          </Section>

          <Section id="schedule-time" eyebrow={copy.time.eyebrow} title={copy.time.title}>
            <p>
              <code className="api-inline-code"> playlist_date </code>
              {copy.time.p1After}
            </p>
            <p>
              <code className="api-inline-code"> elem_start_time </code>
              {copy.time.p2Before}
              <code className="api-inline-code"> elem_end_time </code>
              {copy.time.p2After}
              <code className="api-inline-code"> HH:MM:SS </code>
              /
              <code className="api-inline-code"> HH:MM:SS.mmm</code>.
            </p>
            <p>{copy.time.p3}</p>
          </Section>

          <Section id="schedule-fields" eyebrow={copy.fields.eyebrow} title={copy.fields.title}>
            <h3>{copy.fields.blockTitle}</h3>
            <FieldTable
              columns={[copy.columns.field, copy.columns.type, copy.columns.required, copy.columns.description]}
              rows={copy.blockFields.map(([field, type, required, description]) => [field, type, required ? copy.yes : copy.no, description])}
            />

            <h3 style={{ marginTop: '22px' }}>{copy.fields.elementTitle}</h3>
            <FieldTable
              columns={[copy.columns.field, copy.columns.type, copy.columns.required, copy.columns.description]}
              rows={copy.elementFields.map(([field, type, required, description]) => [field, type, required ? copy.yes : copy.no, description])}
            />
          </Section>

          <Section id="schedule-example" eyebrow={copy.example.eyebrow} title={copy.example.title}>
            <p>{copy.example.p}</p>
            <CodeBlock>{scheduleRequestExample}</CodeBlock>
          </Section>

          <Section id="schedule-response" eyebrow={copy.response.eyebrow} title={copy.response.title}>
            <p>{copy.response.p}</p>
            <CodeBlock>{scheduleResponseExample}</CodeBlock>
          </Section>

          <Section id="schedule-errors" eyebrow={copy.errors.eyebrow} title={copy.errors.title}>
            <div className="api-error-grid">
              {scheduleErrorCodes.map(([status, code]) => (
                <div key={`${status}-${code}`}>
                  <strong>{status}</strong>
                  <span>{code}</span>
                </div>
              ))}
            </div>
            <CodeBlock>{scheduleValidationExample}</CodeBlock>
            <CodeBlock>{scheduleMissingRadioIdExample}</CodeBlock>
            <CodeBlock>{scheduleInvalidRadioIdExample}</CodeBlock>
            <CodeBlock>{scheduleRadioIdMismatchExample}</CodeBlock>
          </Section>

          <Section id="schedule-duplicates" eyebrow={copy.duplicates.eyebrow} title={copy.duplicates.title}>
            <p>{copy.duplicates.p}</p>
            <CodeBlock>{scheduleDuplicateExample}</CodeBlock>
          </Section>

          <Section id="schedule-mistakes" eyebrow={copy.mistakes.eyebrow} title={copy.mistakes.title}>
            <div className="api-error-grid">
              {copy.mistakes.items.map(([title, text]) => (
                <div key={title}>
                  <strong>{title}</strong>
                  <span>{text}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section id="schedule-support" eyebrow={copy.support.eyebrow} title={copy.support.title}>
            <p>
              {copy.support.text}
              <a href="mailto:support@radiomonitor.net">support@radiomonitor.net</a>.
            </p>
          </Section>
        </main>

        <aside className="api-reference-panel" aria-label="Schedule API quick reference">
          <div className="api-reference-card">
            <div className="api-reference-title">{copy.quick.title}</div>
            <div className="api-reference-item">
              <span>{copy.quick.method}</span>
              <strong>POST</strong>
            </div>
            <div className="api-reference-item">
              <span>{copy.quick.internalEndpoint}</span>
              <code>{scheduleEndpointUrl}</code>
            </div>
            <div className="api-reference-item">
              <span>{copy.quick.headers}</span>
              <code>X-API-Key: &lt;your-api-key&gt;</code>
              <code>Content-Type: application/json</code>
            </div>
            <div className="api-reference-item">
              <span>{copy.quick.publicTest}</span>
              <code>{schedulePreviewEndpointUrl}</code>
            </div>
            <div className="api-reference-item">
              <span>{copy.quick.oneFile}</span>
              <code>one playlist_date</code>
              <code>one station</code>
            </div>
            <div className="api-reference-item">
              <span>{copy.quick.blockTypes}</span>
              <code>M / Music</code>
              <code>P / Program</code>
              <code>C / Commerce</code>
              <code>N / News</code>
            </div>
            <div className="api-reference-item">
              <span>{copy.quick.elementClasses}</span>
              <code>M / J / C / B / N</code>
            </div>
            <a className="api-reference-link" href="#schedule-example">
              {copy.quick.link}
            </a>
          </div>
        </aside>
      </div>
    </>
  );
}

function getScheduleBlocks(data) {
  const schedule = data?.schedule;
  if (Array.isArray(schedule)) return schedule;
  if (Array.isArray(schedule?.blocks)) return schedule.blocks;
  if (Array.isArray(data?.blocks)) return data.blocks;
  return [];
}

function getBlockElements(block) {
  if (Array.isArray(block?.elements)) return block.elements;
  if (Array.isArray(block?.items)) return block.items;
  return [];
}

function countBy(items, key) {
  return items.reduce((acc, item) => {
    const value = item?.[key];
    if (value) acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});
}

function getCount(source, key) {
  if (!source) return 0;
  return source[key] ?? source[key.toLowerCase()] ?? 0;
}

function getBlockTypeCount(source, code, label) {
  if (!source) return 0;
  return source[`${code} / ${label}`] ?? source[code] ?? source[code.toLowerCase()] ?? 0;
}

function formatPreviewSuccess(data, sourceJson) {
  const blocks = getScheduleBlocks(data);
  const elements = blocks.flatMap(getBlockElements);
  const blockCounts = data?.block_types || data?.blockTypes || data?.block_type_counts || data?.summary?.block_types || countBy(blocks, 'block_type');
  const elementCounts = data?.elem_classes || data?.element_classes || data?.elementClasses || data?.element_class_counts || data?.summary?.elem_classes || data?.summary?.element_classes || countBy(elements, 'elem_class');
  const radioId = data?.radio_id ?? data?.schedule?.radio_id ?? sourceJson?.radio_id ?? '';
  const playlistDate = data?.playlist_date ?? data?.schedule?.playlist_date ?? sourceJson?.playlist_date ?? '';
  const playlistFile = data?.playlist_file ?? data?.schedule?.playlist_file ?? sourceJson?.playlist_file ?? '';
  const blocksReceived = data?.blocks_received ?? blocks.length;
  const itemsReceived = data?.items_received ?? elements.length;
  const keyValid = data?.key_valid ?? data?.keyValid ?? true;

  const lines = [
    '[OK] Schedule JSON preview',
    `[OK] key_valid: ${keyValid}`,
    `[OK] radio_id: ${radioId}`,
    `[OK] playlist_date: ${playlistDate}`,
    `[OK] playlist_file: ${playlistFile}`,
    `[OK] blocks_received: ${blocksReceived}`,
    `[OK] items_received: ${itemsReceived}`,
    '',
    'Block types:',
    `M / Music: ${getBlockTypeCount(blockCounts, 'M', 'Music')}`,
    `P / Program: ${getBlockTypeCount(blockCounts, 'P', 'Program')}`,
    `C / Commerce: ${getBlockTypeCount(blockCounts, 'C', 'Commerce')}`,
    `N / News: ${getBlockTypeCount(blockCounts, 'N', 'News')}`,
    '',
    'Element classes:',
    `M: ${getCount(elementCounts, 'M')}`,
    `J: ${getCount(elementCounts, 'J')}`,
    `C: ${getCount(elementCounts, 'C')}`,
    `B: ${getCount(elementCounts, 'B')}`,
    `N: ${getCount(elementCounts, 'N')}`,
    '',
    'Full schedule:'
  ];

  blocks.forEach((block) => {
    const blockElements = getBlockElements(block);
    const start = block.block_start_time || block.start_time || '';
    const name = block.block_name || block.name || '(untitled block)';
    const type = block.block_type || '';
    const typeName = block.block_type_name || '';
    lines.push(`[${start}] ${name} | ${type} / ${typeName} | elements: ${blockElements.length}`);

    blockElements.forEach((element) => {
      const elementStart = element.elem_start_time || element.start_time || '';
      const elementEnd = element.elem_end_time || element.end_time || '';
      const elementClass = element.elem_class || element.class || '';
      const elementName = element.elem_name || element.name || '(untitled element)';
      lines.push(`  - ${elementStart} → ${elementEnd} | ${elementClass} | ${elementName}`);
    });
  });

  return lines.join('\n');
}

function formatPreviewError(data) {
  const lines = [
    `[ERROR] ${data?.error || 'request_failed'}`,
    `message: ${data?.message || ''}`
  ];

  if (Array.isArray(data?.details) && data.details.length > 0) {
    lines.push('');
    data.details.forEach((detail) => {
      lines.push(`${detail?.path || '(root)'} -> ${detail?.message || ''}`);
    });
  } else if (data?.details && typeof data.details === 'object') {
    lines.push('');
    Object.entries(data.details).forEach(([key, value]) => {
      lines.push(`${key}: ${value}`);
    });
  }

  return lines.join('\n');
}

function ScheduleJsonTestContent({ copy }) {
  const [apiKey, setApiKey] = React.useState('');
  const [file, setFile] = React.useState(null);
  const [status, setStatus] = React.useState('idle');
  const [terminalOutput, setTerminalOutput] = React.useState(
    copy.test.idle
  );
  const fileInputRef = React.useRef(null);

  function clearForm() {
    setApiKey('');
    setFile(null);
    setStatus('idle');
    setTerminalOutput(copy.test.idle);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!apiKey.trim()) {
      setStatus('error');
      setTerminalOutput(copy.test.missingApiKey);
      return;
    }

    if (!file) {
      setStatus('error');
      setTerminalOutput(copy.test.missingFile);
      return;
    }

    let fileText = '';
    let parsedJson = null;

    try {
      fileText = await file.text();
      parsedJson = JSON.parse(fileText);
    } catch (error) {
      setStatus('error');
      setTerminalOutput(`${copy.test.invalidJson}\nmessage: ${error.message}`);
      return;
    }

    setStatus('loading');
    setTerminalOutput(copy.test.loading);

    try {
      const response = await fetch(schedulePreviewEndpointUrl, {
        method: 'POST',
        headers: {
          'X-API-Key': apiKey.trim(),
          'Content-Type': 'application/json'
        },
        body: fileText
      });

      const responseText = await response.text();
      let responseJson = null;

      try {
        responseJson = responseText ? JSON.parse(responseText) : null;
      } catch {
        responseJson = null;
      }

      if (!response.ok) {
        setStatus('error');
        if (responseJson) {
          setTerminalOutput(formatPreviewError(responseJson));
        } else {
          setTerminalOutput(`[ERROR] HTTP ${response.status}\n${responseText}`);
        }
        return;
      }

      if (responseJson?.ok === false) {
        setStatus('error');
        setTerminalOutput(formatPreviewError(responseJson));
        return;
      }

      setStatus('success');
      setTerminalOutput(formatPreviewSuccess(responseJson || {}, parsedJson));
    } catch (error) {
      setStatus('error');
      setTerminalOutput(`${copy.test.networkError}\nmessage: ${error.message}`);
    }
  }

  return (
    <>
      <header className="api-hero">
        <div>
          <div className="api-kicker">{copy.kicker}</div>
          <h1>{copy.test.heroTitle}</h1>
          <p>{copy.test.heroText}</p>
        </div>
        <div className="api-hero-panel" aria-label="Schedule preview endpoint">
          <span className="api-method api-method-post">POST</span>
          <code>{schedulePreviewEndpointUrl}</code>
        </div>
      </header>

      <div className="api-test-panel">
        <form className="api-test-form" onSubmit={handleSubmit}>
          <div className="api-test-field">
            <label className="api-test-label" htmlFor="schedule-api-key">{copy.test.apiKey}</label>
            <input
              className="api-test-input"
              id="schedule-api-key"
              type="password"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              autoComplete="off"
            />
          </div>

          <div className="api-test-field">
            <label className="api-test-label" htmlFor="schedule-json-file">{copy.test.jsonFile}</label>
            <input
              className="api-test-file"
              id="schedule-json-file"
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              onChange={(event) => setFile(event.target.files?.[0] || null)}
            />
          </div>

          <div className="api-test-actions">
            <button className="api-test-button" type="submit" disabled={status === 'loading'}>
              {copy.test.testButton}
            </button>
            <button className="api-test-button api-test-button-secondary" type="button" onClick={clearForm}>
              {copy.test.clearButton}
            </button>
          </div>
        </form>

        <div className="api-terminal" aria-live="polite">
          <div className="api-terminal-header">
            <div className="api-terminal-title">{copy.test.terminalTitle}</div>
            <button className="api-terminal-clear" type="button" onClick={clearForm}>
              {copy.test.clearButton}
            </button>
          </div>
          <pre className="api-terminal-body">{terminalOutput}</pre>
        </div>
      </div>
    </>
  );
}

export default function ApiPage() {
  const loc = useLocation();
  const lang = getLangFromPath(loc.pathname);
  const copy = translations[lang] || translations.uk;
  const pageCopy = apiPageUi[lang] || apiPageUi.uk;
  const scheduleCopy = getScheduleCopy(lang);
  const [activeApi, setActiveApi] = React.useState('ingest');

  return (
    <div className="api-page">
      <h1 className="api-page-title">{pageCopy.title}</h1>
      <div className="api-page-actions">
        <button
          className={`api-page-action-btn${activeApi === 'ingest' ? ' is-active' : ''}`}
          type="button"
          aria-pressed={activeApi === 'ingest'}
          onClick={() => setActiveApi('ingest')}
        >
          {pageCopy.tabs.ingest}
        </button>
        <button
          className={`api-page-action-btn${activeApi === 'schedule' ? ' is-active' : ''}`}
          type="button"
          aria-pressed={activeApi === 'schedule'}
          onClick={() => setActiveApi('schedule')}
        >
          {pageCopy.tabs.schedule}
        </button>
        <button
          className={`api-page-action-btn${activeApi === 'scheduleTest' ? ' is-active' : ''}`}
          type="button"
          aria-pressed={activeApi === 'scheduleTest'}
          onClick={() => setActiveApi('scheduleTest')}
        >
          {pageCopy.tabs.test}
        </button>
      </div>

      {activeApi === 'scheduleTest' ? (
        <ScheduleJsonTestContent copy={scheduleCopy} />
      ) : activeApi === 'schedule' ? (
        <ScheduleApiContent copy={scheduleCopy} />
      ) : (
        <IngestApiContent copy={copy} />
      )}
    </div>
  );
}
